/**
 * Governance → Desktop mascot gallery, without React (unit-tested in apps/admin/test/desktop-mascot.test.ts). IT admins
 * upload outfits for the floating Gyro and choose which ones are in the gallery each person picks from in Gyro's
 * settings: the built-in Gyro always, a few outfits for good, and some that rotate each month (owner, 2026-10-05). Any
 * number of outfits can be featured, say one for each season (owner, 2026-10-05: Pilgrim in November, Santa in
 * December): people who have not picked an outfit get the first featured outfit, in the order the outfits were added,
 * that is in the gallery now, else the built-in Gyro. Only the floating bubble changes; the chat avatar does not. This
 * file holds:
 *  - the limits gyro-svc enforces, mirrored from services/gyro-svc/src/desktopMascotPng.ts and adminSchema.ts and
 *    cross-tested against them;
 *  - a check of the chosen file's PNG header and chunks, run before anything is sent, with the service's own sentences;
 *  - ports of the desktop agent's trim and fit rules (desktop/src/Gyro.Core/Ui/MascotGeometry.cs), so the preview is
 *    drawn the way the bubble draws him;
 *  - the pure steps of "Prepare for desktops" (mirror, alpha clean-up, trim with a margin, fit, shrink until the bytes
 *    fit), which the card runs on a canvas when a file breaks a size or byte limit or the admin asks for a mirror;
 *  - the gallery window rules (when an outfit is in the gallery, scheduled or ended), the monthly rotation helper, and
 *    the yearly windows ("Every year in…" chosen months, by the calendar in Pacific time), ported from
 *    services/gyro-svc/src/desktopMascotWindow.ts and cross-tested against it;
 *  - which featured outfit people who have not picked one get now and next (defaultLook, featuredTimeline), the same rule
 *    as that file's featuredInGallery and cross-tested against it, and the Featured toggle's body;
 *  - what the service adds for day outfits (owner, 2026-10-06), retired outfits and star ratings: the view's types, which
 *    catalog outfits the gallery grid shows (galleryOutfits: never a day outfit, which the Day outfits section lists, nor
 *    a retired outfit's leftover copy), and the limit (2,000 kept in all, retired ones too; past the first 100 the service
 *    keeps outfits beyond its catalog, in the gallery all the same);
 *  - the card's wording: what people see by default, readiness, warnings, refusals, history and the retire confirmation.
 *    An outfit is never deleted: Retire keeps it, with its id, image and ratings, for the Hub's costume shop, and Restore
 *    brings it back under the same id.
 * The day outfits' own rules, wording and bodies are in lib/dayOutfits.ts. The console is advisory only: gyro-svc parses
 * the whole image again, and so does every agent before it decodes it.
 */
import type { SupportDevice } from './api';
import type { DayRule } from './mascotDays';
import type { CategoriesView } from './outfitCategories';
import type { OutfitFacingView } from './outfitFacing';

// ---- Limits (cross-tested against the service) ----

export const MASCOT_LIMITS = {
  /** desktopMascotPng.ts MASCOT_MAX_BYTES: keeps the base64 inside the JSON body under the service's 1 MiB limit. */
  maxBytes: 768_000,
  /** adminSchema.ts: pngBase64 at most this many characters (maxBytes as base64). */
  maxBase64Chars: 1_024_000,
  maxSide: 1024,
  minLongSide: 256,
  maxAspect: 2,
  /** At least this share of the box around the visible pixels must be fully transparent. */
  minCutout: 0.15,
  maxChunks: 4096,
  /** A gallery window's end is at most this many days from now when it is set or changed, and its start at most this many days ago. */
  maxShowUntilDays: 366,
  /** ...and its end at least this far ahead. */
  minShowUntilLeadMs: 5 * 60_000,
  /** adminSchema.ts DESKTOP_MASCOT_LABEL_MAX: the #286 label, which history entries carried over from it may hold. */
  labelMax: 80,
  /** desktopMascotPng.ts MASCOT_NAME_MAX: an outfit's name, after trimming. */
  nameMax: 40,
  /**
   * desktopMascotPng.ts MASCOT_MAX_OUTFITS: the most outfits the service's catalog document holds (all a service from
   * before outfits were kept beyond it shows after a rollback, and the most such a service's gallery holds at once, which
   * mascotLimitsOf assumes when a service sends no limits). The service now keeps outfits past it beyond the catalog
   * (`placement: 'overflow'`), in the gallery all the same, and says the gallery holds maxTotalOutfits at once.
   */
  maxOutfits: 100,
  /**
   * desktopMascotPng.ts MASCOT_MAX_TOTAL_OUTFITS: every outfit Gyro keeps, retired ones included (each stays a product for
   * the costume shop), so retiring one makes no room here.
   */
  maxTotalOutfits: 2000,
} as const;

/** Desktop agents at or above this version show the person's own pick from the gallery. */
export const MASCOT_MIN_AGENT_VERSION = '0.4.4.0';
/** Agent 0.4.3.8 (#286) asks for no outfit: it shows the featured outfit while it is in the gallery, never a person's pick. */
export const MASCOT_FEATURED_AGENT_VERSION = '0.4.3.8';

/** The bubble sizes the preview offers: the smallest and largest a person can pick (44 to 200), and the default 76. */
export const BUBBLE_SIZES = [44, 76, 200] as const;
export const DEFAULT_BUBBLE_SIZE = 76;
/** A trimmed longer side under this looks soft at the largest bubble size (200 at 100% scaling). */
export const MASCOT_WARN_LONG_SIDE = 200;
/** Alpha below this is haze: clickable on the desktop, but next to invisible. */
export const MASCOT_HAZE_ALPHA = 16;
/** Haze is worth a warning only when it moves the clickable box this many pixels past the art (anti-aliasing does not). */
export const MASCOT_HAZE_TOLERANCE_PX = 4;

/**
 * Which art the desktop mirrors: the packaged Gyro is flipped so he looks left (desktop BubbleWindow.cs), and an admin's
 * image is shown exactly as uploaded (owner decision, 2026-10-05). The preview follows the same rule.
 */
export const MASCOT_MIRROR = { packaged: true, custom: false } as const;
export const MASCOT_DRAW_HINT = 'Draw him facing left; an outfit drawn facing right is mirrored when it is uploaded, so every outfit faces left on desktops.';
export const MASCOT_POLL_HINT = 'Desktops check about every 15 minutes and whenever Gyro starts, so a change reaches PCs within about 20 minutes.';
/** The rule behind the Featured toggle, in the card's words (gyro-svc featuredNow decides it the same way). */
export const MASCOT_FEATURED_HINT = 'People who have not picked an outfit get the first featured outfit that is in the gallery now, '
  + 'in the order the outfits were added, otherwise the built-in Gyro. Feature an outfit for each season and each one takes over in its own months.';

/** "Prepare for desktops": the steps the card applies on a canvas (the owner's 1254x1254, 1.4 MB export needed all of them). */
export const PREPARE = {
  /** Alpha at or below this becomes 0 (haze). */
  hazeAlpha: 15,
  /** Alpha at or above this becomes 255 (a nearly solid body). */
  solidAlpha: 240,
  /** Transparent border kept around the trimmed art, in source pixels. */
  marginPx: 6,
  /** Each re-encode that is still over the byte limit shrinks the longer side by about 6%. */
  shrinkFactor: 0.94,
  /** ...but never below this; past it the card refuses. */
  floorPx: 512,
} as const;

const DAY_MS = 24 * 60 * 60_000;

// ---- What the service answers (/v1/admin/desktop/mascot and its outfits, services/gyro-svc/src/desktopMascot.ts) ----

/**
 * When an outfit is in the gallery: always; from startMs (included) to endMs (not included), epoch ms; or every year in
 * these months, 1 (January) to 12 (December), each once, in calendar order, by the calendar in Pacific time
 * (MASCOT_TIME_ZONE).
 */
export type MascotWindow =
  | { kind: 'always' }
  | { kind: 'between'; startMs: number; endMs: number }
  | { kind: 'yearly'; months: number[] };
export type BetweenWindow = Extract<MascotWindow, { kind: 'between' }>;
/** What desktops receive for a window: a yearly one as its occurrence going on now, or its next one (effectiveWindow). */
export type OccurrenceWindow = Exclude<MascotWindow, { kind: 'yearly' }>;

/** In the gallery now, not yet (before its start), or no longer (from its end). A yearly window is never ended. */
export type OutfitState = 'in-gallery' | 'scheduled' | 'ended';

/** A day outfit's kind: a holiday, or a fun day (the import's two kinds). */
export type DayOutfitKind = 'holiday' | 'fun';
/** What an outfit is, as the service groups it: a day outfit's kind, else seasonal for a yearly window, else regular. */
export type OutfitKind = DayOutfitKind | 'seasonal' | 'regular';

/**
 * An outfit's stars as the console sees them (the service's AdminRatingView): everyone's average, to one decimal (null
 * when nobody rated it), how many people rated it, and how many gave it 1, 2, 3, 4 and 5 stars (`distribution`, in that
 * order). Never who rated it, or how.
 */
export interface OutfitRating {
  average: number | null;
  count: number;
  distribution: number[];
}

export interface AdminOutfit {
  /** Made by the service: "o-" and 10 lowercase letters or digits. */
  id: string;
  name: string;
  sha256: string;
  sizeBytes: number;
  width: number;
  height: number;
  window: MascotWindow;
  /**
   * Only for a yearly window: its occurrence going on now, or its next one, when the service answered (what desktops
   * receive); the card works it out again with effectiveWindow as time passes.
   */
  occurrence?: OccurrenceWindow;
  /** The state when the service answered; the card works it out again with outfitState as time passes. */
  state: OutfitState;
  /** Featured, whatever its window; any number of outfits may be (MASCOT_FEATURED_HINT). */
  featured: boolean;
  /**
   * Base64 PNG trimmed to the visible pixels, the longer side at most 128 px; shown exactly as stored, never mirrored.
   * Absent for an outfit kept beyond the catalog's 100, whose thumbnail is at `thumbnailUrl` (outfitPicture).
   */
  thumbnailPng?: string;
  /**
   * The service's route for the thumbnail PNG of an outfit kept beyond the catalog (`placement: 'overflow'`), which it
   * sends by URL so its answers stay as small as with 100 outfits. Absent for every other outfit.
   */
  thumbnailUrl?: string;
  addedBy: string;
  addedAtMs: number;
  updatedBy: string;
  updatedAtMs: number;
  /**
   * What it is (OutfitKind). A day outfit's catalog window is only the day a gyro-svc from before day outfits shows it
   * on; its dates come from its rule, in the catalog's `days`. Absent from a service before day outfits.
   */
  kind?: OutfitKind;
  /**
   * Where it is stored: the catalog (its first 100), or beyond it ('overflow'), which a service from before outfits were
   * kept there does not show after a rollback. Absent from a service before day outfits.
   */
  placement?: 'catalog' | 'overflow';
  /** Its stars; absent while the service cannot read the ratings, and from a service before them. */
  rating?: OutfitRating;
  /**
   * Its outfit category's id (lib/outfitCategories.ts), or null for General, also while the service cannot read the
   * categories. Absent from a service before categories.
   */
  category?: string | null;
  /**
   * Which way it faces and whether it was mirrored on the way in (lib/outfitFacing.ts). Absent until the service has
   * judged it (the facing backfill), and from a service before facings.
   */
  facing?: OutfitFacingView;
  /**
   * Whether clicking the mascot in this outfit plays an action (an eligible animation pack bound to it): the tile
   * grid's Animation filter (owner, 2026-10-19). Absent while the service has mascot animations off or cannot read the
   * bindings, and from a service before the filter; the filter is then not offered (animationFilterAvailable).
   */
  hasActions?: boolean;
}

/** Where a day outfit is in its life, by the Pacific calendar: on today, on a later date, waiting for IT to enter its next date, or a one-off that has gone. */
export type DayState = 'today' | 'scheduled' | 'needs-date' | 'past';

/**
 * A day outfit as the service describes it (its AdminDayView), by outfit id in the catalog's `days`: its rule and that
 * rule in words, the greeting people see on its day in place of "What can we work on?", its kind, collection and import
 * key, its next three days and its state by the Pacific calendar when the service answered, and whether its day was
 * going on anywhere on Earth then (UTC+14 to UTC-12). The card works the dates and states out again as time passes
 * (lib/dayOutfits.ts).
 */
export interface AdminDay {
  rule: DayRule;
  ruleText: string;
  greeting: string;
  kind: DayOutfitKind;
  collection: string | null;
  importKey: string | null;
  /** Up to three start dates, 'YYYY-MM-DD', by the Pacific calendar. */
  nextDates: string[];
  state: DayState;
  showingSomewhereNow: boolean;
}

/**
 * An outfit kept outside the catalog (the service's AdminKeptView): retired (Retire keeps every outfit), or one that
 * never reached the gallery (an add that did not finish, or a day outfit an older gyro-svc's console deleted). Either
 * comes back under its own id with Restore. No thumbnail: the picture is at `imageUrl`.
 */
export interface KeptOutfit {
  id: string;
  name: string;
  sha256: string;
  sizeBytes: number;
  width: number;
  height: number;
  addedBy: string;
  addedAtMs: number;
  importKey: string | null;
  /** A day outfit's kind; null for any other outfit. */
  kind: DayOutfitKind | null;
  /** A day outfit's rule in words. */
  ruleText?: string;
  /** The admin image route for its PNG (admins and auditors only), loaded lazily. */
  imageUrl: string;
  updatedBy: string;
  updatedAtMs: number;
  /** Whether it was featured when it was retired (Restore brings it back unfeatured); absent from an older service. */
  wasFeatured?: boolean;
  /** The stars it kept (a retired outfit keeps them for the shop); absent while the ratings cannot be read. */
  rating?: OutfitRating;
}

/**
 * A line of the service's other history (its AdminAnnexHistoryLine), which History lists beside the catalog's: a day
 * outfit's new day or greeting ('day'), a retire, a restore. `withCatalogLine`: the catalog's history has a line for the
 * same change too (a retire's 'remove', a restore's 'add'), which History shows as this one line.
 */
export interface AnnexHistoryLine {
  action: string;
  outfitId: string;
  name: string;
  by: string;
  atMs: number;
  withCatalogLine: boolean;
  /**
   * Only on a line about an outfit kept beyond the catalog ('overflow-add', 'overflow-edit'): the change, as a catalog
   * line's action names it. Absent from an older service.
   */
  change?: 'add' | 'edit' | 'feature' | 'unfeature';
}

/**
 * Something about the day outfits the service warns of (its AdminDayWarning), with how much it matters:
 * - clash: day outfits of the same length on one Pacific date in the next 366 days: the first in catalog order (winnerId)
 *   is swapped in, the others (loserIds) are only in the gallery that day;
 * - span-note: a shorter day outfit takes a date inside a longer one (Gingerbread House Day inside Hanukkah), as meant;
 * - missing-next-year: a rule with dates IT enters has none for next year (Pacific); an error when it has none to come;
 * - confirm-each-year: a date announced later each year, to be checked;
 * - leap-only: February 29, which comes only in leap years;
 * - newer-rule: a day rule from a newer release of Gyro, so the outfit is hidden here;
 * - pending-tidy: retired, but its copy is still in the catalog (a retire that did not finish).
 */
export type DayWarning =
  | { kind: 'clash'; severity: 'warning'; date: string; winnerId: string; loserIds: string[] }
  | { kind: 'span-note'; severity: 'info'; date: string; winnerId: string; loserIds: string[] }
  | { kind: 'missing-next-year'; severity: 'warning' | 'error'; outfitId: string; year: number }
  | { kind: 'confirm-each-year'; severity: 'info'; outfitId: string; nextDate: string | null }
  | { kind: 'leap-only'; severity: 'info'; outfitId: string; nextDate: string | null }
  | { kind: 'newer-rule'; severity: 'warning'; outfitId: string }
  | { kind: 'pending-tidy'; severity: 'warning'; outfitId: string };

/**
 * The gallery's limits as the service answers them, how much of the total is used (null while it cannot tell), and
 * where the gallery's outfits are kept.
 */
export interface MascotLimitsView {
  /**
   * The most outfits the gallery holds at once: MASCOT_LIMITS.maxTotalOutfits from a service that keeps outfits past its
   * catalog's 100 beyond it (`placement: 'overflow'`), MASCOT_LIMITS.maxOutfits from an older one (retiring one made room).
   */
  catalog: number;
  /** Every outfit kept, retired ones included (MASCOT_LIMITS.maxTotalOutfits): retiring makes no room. */
  total: number;
  used: number | null;
  /**
   * How many of the gallery's outfits are in the service's catalog (at most MASCOT_LIMITS.maxOutfits, all an older
   * service shows after a rollback) and how many beyond it (null while the service cannot tell). Absent from an older
   * service.
   */
  placement?: { catalog: number; overflow: number | null };
}

/** publish, schedule and reset are #286 entries the migration carried over. */
export type MascotHistoryAction = 'add' | 'edit' | 'remove' | 'feature' | 'unfeature' | 'publish' | 'schedule' | 'reset';

export interface MascotHistoryEntry {
  action: MascotHistoryAction;
  outfitId: string | null;
  name: string | null;
  sha256: string | null;
  /**
   * Never yearly: the service keeps no window on the line of a change to a yearly one (a gyro-svc from before the yearly
   * windows could not read its catalog back), so that line reads without one; its months are in the audit trail.
   */
  window: OccurrenceWindow | null;
  by: string;
  atMs: number;
}

export interface DesktopMascotCatalog {
  /** The revision every write sends back as expectedRevision; a stale one is refused with 409 mascot_changed. */
  revision: number;
  /**
   * The first featured outfit in catalog order, or null: the featured outfit while at most one is, as before several
   * could be. The card reads each outfit's `featured` instead.
   */
  featuredId: string | null;
  /** The featured outfit people who have not picked one got when the service answered; defaultLook works it out as time passes. */
  featuredNowId: string | null;
  /** Catalog order: oldest added first. Day outfits are here too; galleryOutfits leaves them out. */
  outfits: AdminOutfit[];
  /** Newest first, at most 50. */
  history: MascotHistoryEntry[];
  /** People by their stored pick ("default" or an outfit id). Automatic is not counted, and a key is absent at 0. */
  picks: Record<string, number>;
  /**
   * What day outfits, Retire and Restore send back as expectedAnnexRevision; a stale one is refused with 409
   * mascot_changed. Null while the service cannot read where it keeps day outfits (then `days`, `retired`, `orphans` and
   * `dayWarnings` are empty and `limits.used` is null); absent from a service before day outfits.
   */
  annexRevision?: number | null;
  /** Each day outfit in the catalog, by outfit id: a catalog outfit listed here is a day outfit. */
  days?: Record<string, AdminDay>;
  /** Retired outfits, kept for the costume shop (Restore brings one back). */
  retired?: KeptOutfit[];
  /** Outfits that never reached the gallery (Restore finishes adding one; Retire keeps it out). */
  orphans?: KeptOutfit[];
  /** The service's other history, newest first: day changes, retires and restores (galleryHistory lists it). */
  annexHistory?: AnnexHistoryLine[];
  dayWarnings?: DayWarning[];
  limits?: MascotLimitsView;
  /**
   * False while the service has day outfits switched off (GYRO_DAY_OUTFITS=0): nobody gets one or its greeting, and
   * the Day outfits section says so. Absent from an older service.
   */
  dayOutfitsOn?: boolean;
  /** The built-in Gyro's `hasActions` (its own binding): present exactly when the outfits' is, absent otherwise. */
  builtInHasActions?: boolean;
  /**
   * The outfit categories (lib/outfitCategories.ts): the registry with each category's state today. Null while the
   * service cannot read them; absent from a service before categories.
   */
  categories?: CategoriesView | null;
}

export interface DesktopMascotAdded extends DesktopMascotCatalog {
  outfitId: string;
  /** Chunk types the service removed from the uploaded file before storing it. */
  strippedChunks: string[];
}

export interface AddMascotOutfitBody {
  pngBase64: string;
  name: string;
  window: MascotWindow;
  featured?: boolean;
  /** The category the outfit goes in (its id); left out: General. */
  category?: string;
  expectedRevision: number;
}

/**
 * POST …/outfits for a day outfit: its rule instead of a window (it is never featured), its greeting and kind, and both
 * revisions the card loaded. The console never sends an import key (the import tool's).
 */
export interface AddDayOutfitBody {
  pngBase64: string;
  name: string;
  day: DayRule;
  greeting: string;
  kind: DayOutfitKind;
  collection?: string | null;
  expectedRevision: number;
  expectedAnnexRevision: number;
}

export interface UpdateMascotOutfitBody {
  name?: string;
  window?: MascotWindow;
  /** The Featured toggle: the outfit joins or leaves the featured outfits, and the others stay as they are. */
  featured?: boolean;
  /** The outfit's category: a category id, or null for General. Left out: unchanged. */
  category?: string | null;
  expectedRevision: number;
  /**
   * For an outfit kept beyond the catalog (`placement: 'overflow'`), whose changes are the service's annex: the annex
   * revision the card loaded, refused with 409 mascot_changed when stale (withAnnexRevision adds it).
   */
  expectedAnnexRevision?: number;
}

/**
 * A PATCH body for `outfit`, with the annex revision the card loaded when the outfit is kept beyond the catalog (its
 * changes go to the annex, whose revision is then the compare-and-swap token as the catalog's is for the others); the
 * body as it is otherwise, and while the card has no annex revision.
 */
export function withAnnexRevision(body: UpdateMascotOutfitBody, outfit: Pick<AdminOutfit, 'placement'> | undefined, annexRevision: number | null | undefined): UpdateMascotOutfitBody {
  return outfit?.placement === 'overflow' && typeof annexRevision === 'number' ? { ...body, expectedAnnexRevision: annexRevision } : body;
}

/**
 * A gallery outfit's picture: its thumbnail inline (an outfit in the catalog), or the service's thumbnail route for one
 * kept beyond the catalog (`thumbnailUrl`; the route by its id should a service name none), loaded lazily.
 */
export function outfitPicture(outfit: Pick<AdminOutfit, 'id' | 'thumbnailPng' | 'thumbnailUrl'>): { thumbnailPng: string } | { imageUrl: string } {
  if (typeof outfit.thumbnailPng === 'string') return { thumbnailPng: outfit.thumbnailPng };
  return { imageUrl: outfit.thumbnailUrl ?? `/v1/admin/desktop/mascot/outfits/${encodeURIComponent(outfit.id)}/thumbnail` };
}

/** The src of an outfit's tile (outfitPicture): a data URL of its thumbnail, or the thumbnail route. */
export function outfitThumbnailSrc(outfit: Pick<AdminOutfit, 'id' | 'thumbnailPng' | 'thumbnailUrl'>): string {
  const picture = outfitPicture(outfit);
  return 'thumbnailPng' in picture ? `data:image/png;base64,${picture.thumbnailPng}` : picture.imageUrl;
}

/**
 * PUT …/outfits/:id/day: a day outfit's rule, greeting, kind and collection (null takes the collection away; left out,
 * it stays). It changes the annex revision, never the catalog revision.
 */
export interface UpdateDayBody {
  day: DayRule;
  greeting: string;
  kind: DayOutfitKind;
  collection?: string | null;
  expectedRevision: number;
  expectedAnnexRevision: number;
}

/** POST …/outfits/:id/restore: both revisions the card loaded. */
export interface RestoreOutfitBody {
  expectedRevision: number;
  expectedAnnexRevision: number;
}

// ---- The PNG check (the service's rules and sentences, without the CRC and pixel decode) ----

export type MascotRefusalReason =
  | 'too_large'
  | 'not_png'
  | 'bad_ihdr'
  | 'bad_dimensions'
  | 'bad_aspect'
  | 'not_rgba8'
  | 'interlaced'
  | 'animated'
  | 'unsupported_chunk'
  | 'bad_image_data'
  | 'trailing_bytes'
  | 'no_visible_pixels'
  | 'no_transparency';

export interface MascotRefusal {
  reason: MascotRefusalReason;
  message: string;
}

export interface MascotPngHeader {
  width: number;
  height: number;
  bitDepth: number;
  colorType: number;
  /** Every chunk type, named once, in the order they first appear. */
  chunkTypes: string[];
  /** Ancillary chunks the service removes before storing the file (all but sRGB, gAMA and pHYs). */
  removed: string[];
}

export interface MascotPngCheck {
  /** Null when the file is not a PNG with a sound header (nothing here can read or prepare it). */
  header: MascotPngHeader | null;
  /** The first rule the file breaks, in the service's order, or null. */
  refusal: MascotRefusal | null;
  /** Over the byte limit or wider or taller than the limit: what "Prepare for desktops" fixes by itself. */
  overLimits: boolean;
}

const SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const KEPT_ANCILLARY = ['sRGB', 'gAMA', 'pHYs'];
const ANIMATION_CHUNKS = ['acTL', 'fcTL', 'fdAT'];
const COLOR_TYPES: Record<number, string> = {
  0: 'greyscale',
  2: 'RGB without alpha',
  3: 'palette colour',
  4: 'greyscale with alpha',
  6: 'RGBA',
};
const DAMAGED = 'Export the image again.';

const count = (value: number): string => value.toLocaleString('en-US');
const refuse = (reason: MascotRefusalReason, message: string): MascotRefusal => ({ reason, message });

function u32(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] << 24) >>> 0) + (bytes[offset + 1] << 16) + (bytes[offset + 2] << 8) + bytes[offset + 3];
}

function chunkName(bytes: Uint8Array, offset: number): string {
  return String.fromCharCode(bytes[offset], bytes[offset + 1], bytes[offset + 2], bytes[offset + 3]);
}

/** The chunk walk: names, what the service removes, and the first structural rule broken. */
function walkChunks(bytes: Uint8Array): { chunkTypes: string[]; removed: string[]; refusal: MascotRefusal | null } {
  const chunkTypes: string[] = [];
  const removed: string[] = [];
  const done = (refusal: MascotRefusal | null) => ({ chunkTypes, removed, refusal });
  let idat: 'before' | 'inside' | 'after' = 'before';
  let sawIdat = false;
  let chunks = 0;
  let offset = 8;
  while (offset < bytes.length) {
    chunks += 1;
    if (chunks > MASCOT_LIMITS.maxChunks) {
      return done(refuse('bad_image_data', `The PNG is split into more than ${count(MASCOT_LIMITS.maxChunks)} chunks. Export it again with an ordinary image editor.`));
    }
    if (bytes.length - offset < 12) return done(refuse('bad_image_data', `The PNG file is incomplete: it stops in the middle of a chunk. ${DAMAGED}`));
    const length = u32(bytes, offset);
    if (length > bytes.length - offset - 12) return done(refuse('bad_image_data', `The PNG file is incomplete: a chunk runs past the end of the file. ${DAMAGED}`));
    const type = chunkName(bytes, offset + 4);
    if (!/^[A-Za-z]{4}$/.test(type)) return done(refuse('bad_image_data', `The PNG file is damaged: a chunk has an invalid name. ${DAMAGED}`));
    if (!chunkTypes.includes(type)) chunkTypes.push(type);
    const next = offset + 12 + length;
    if (offset === 8) {
      // The IHDR, checked by the caller.
    } else if (type === 'IHDR') {
      return done(refuse('bad_ihdr', `The PNG has more than one header (IHDR). ${DAMAGED}`));
    } else if (type === 'IDAT') {
      if (idat === 'after') return done(refuse('bad_image_data', `The PNG image data is split up by other chunks. ${DAMAGED}`));
      idat = 'inside';
      sawIdat = true;
    } else if (type === 'IEND') {
      if (length !== 0) return done(refuse('bad_image_data', `The PNG end marker (IEND) is damaged. ${DAMAGED}`));
      if (next < bytes.length) return done(refuse('trailing_bytes', `The PNG has extra data after its end marker (IEND). ${DAMAGED}`));
      return done(sawIdat ? null : refuse('bad_image_data', `The PNG has no image data. ${DAMAGED}`));
    } else {
      if (idat === 'inside') idat = 'after';
      if (ANIMATION_CHUNKS.includes(type)) return done(refuse('animated', 'The PNG is animated (APNG). Desktops show a still image; export a single frame.'));
      if ((type.charCodeAt(0) & 0x20) === 0) {
        return done(refuse('unsupported_chunk', `The PNG contains a ${type} chunk, which desktops do not accept. Export it again as a plain 32-bit PNG.`));
      }
      if (!KEPT_ANCILLARY.includes(type) && !removed.includes(type)) removed.push(type);
    }
    offset = next;
  }
  return done(refuse('bad_image_data', `The PNG file is incomplete: it has no end marker (IEND). ${DAMAGED}`));
}

/**
 * Check a chosen file's bytes before anything is sent: the PNG signature and header first (a file that is not a PNG
 * cannot be prepared either), then the service's rules in the service's order. Never trusts the file name or type.
 */
export function checkMascotPng(bytes: Uint8Array): MascotPngCheck {
  if (bytes.length < SIGNATURE.length || SIGNATURE.some((value, index) => bytes[index] !== value)) {
    return { header: null, refusal: refuse('not_png', 'The file is not a PNG image. Export the mascot as a PNG with a transparent background.'), overLimits: false };
  }
  if (bytes.length < 33 || u32(bytes, 8) !== 13 || chunkName(bytes, 12) !== 'IHDR') {
    return { header: null, refusal: refuse('bad_ihdr', `The PNG header (IHDR) is missing or damaged. ${DAMAGED}`), overLimits: false };
  }
  const width = u32(bytes, 16);
  const height = u32(bytes, 20);
  const [bitDepth, colorType, compression, filterMethod, interlace] = [bytes[24], bytes[25], bytes[26], bytes[27], bytes[28]];
  const walk = walkChunks(bytes);
  const header: MascotPngHeader = { width, height, bitDepth, colorType, chunkTypes: walk.chunkTypes, removed: walk.removed };
  const longer = Math.max(width, height);
  const shorter = Math.min(width, height);
  const overLimits = bytes.length > MASCOT_LIMITS.maxBytes || longer > MASCOT_LIMITS.maxSide;
  let refusal: MascotRefusal | null;
  if (bytes.length > MASCOT_LIMITS.maxBytes) {
    refusal = refuse('too_large', `The image is ${count(bytes.length)} bytes; a desktop mascot may be at most ${count(MASCOT_LIMITS.maxBytes)} bytes. Export it smaller: 1024 x 1024 pixels is plenty.`);
  } else if (shorter === 0 || longer > MASCOT_LIMITS.maxSide || longer < MASCOT_LIMITS.minLongSide) {
    refusal = refuse('bad_dimensions', `The image is ${width} x ${height} pixels. Each side may be at most ${count(MASCOT_LIMITS.maxSide)} pixels, and the longer side must be at least ${MASCOT_LIMITS.minLongSide} pixels.`);
  } else if (longer / shorter > MASCOT_LIMITS.maxAspect) {
    refusal = refuse('bad_aspect', `The image is ${width} x ${height} pixels. Its longer side may be at most twice its shorter side.`);
  } else if (bitDepth !== 8 || colorType !== 6) {
    refusal = refuse('not_rgba8', `The PNG is ${bitDepth}-bit ${COLOR_TYPES[colorType] ?? `colour type ${colorType}`}; desktops need 8-bit RGBA (colour with an alpha channel). Export it as a 32-bit PNG with transparency.`);
  } else if (compression !== 0 || filterMethod !== 0 || interlace > 1) {
    refusal = refuse('bad_ihdr', `The PNG header names a compression, filter or interlace method that PNG does not define. ${DAMAGED}`);
  } else if (interlace === 1) {
    refusal = refuse('interlaced', 'The PNG is interlaced. Export it again with interlacing switched off.');
  } else {
    refusal = walk.refusal;
  }
  return { header, refusal, overLimits };
}

// ---- Geometry: ports of the agent's MascotGeometry ----

export interface MascotRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MascotSize {
  width: number;
  height: number;
}

/**
 * MascotGeometry.OpaqueBounds: the tight box around every pixel whose alpha is at least `threshold`, in a row-major
 * alpha plane; null when no pixel qualifies or the plane is shorter than width x height. Threshold 0 counts as 1.
 */
export function opaqueBounds(alpha: ArrayLike<number>, width: number, height: number, threshold = 1): MascotRect | null {
  if (width <= 0 || height <= 0 || alpha.length < width * height) return null;
  const minimum = threshold === 0 ? 1 : threshold;
  let minX = Number.MAX_SAFE_INTEGER;
  let minY = Number.MAX_SAFE_INTEGER;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    const row = y * width;
    for (let x = 0; x < width; x += 1) {
      if (alpha[row + x] < minimum) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }
  if (maxX < 0) return null;
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

/** .NET Math.Round's default: a value exactly halfway goes to the even neighbour (2.5 to 2, 3.5 to 4). */
function roundHalfEven(value: number): number {
  const floor = Math.floor(value);
  const fraction = value - floor;
  if (fraction > 0.5) return floor + 1;
  if (fraction < 0.5) return floor;
  return floor % 2 === 0 ? floor : floor + 1;
}

/**
 * MascotGeometry.FitInSquare: the longer side becomes `sizePx` and the other follows the aspect, never below 1 px; a
 * degenerate source fits as a square. Rounds as .NET does, so the preview is the agent's size to the pixel.
 */
export function fitInSquare(sizePx: number, srcWidth: number, srcHeight: number): MascotSize {
  const size = Math.max(1, sizePx);
  if (srcWidth <= 0 || srcHeight <= 0) return { width: size, height: size };
  if (srcWidth >= srcHeight) return { width: size, height: Math.max(1, roundHalfEven(size * srcHeight / srcWidth)) };
  return { width: Math.max(1, roundHalfEven(size * srcWidth / srcHeight)), height: size };
}

/** The share of pixels inside `bounds` whose alpha is exactly 0, from 0 to 1. */
export function transparentShare(alpha: ArrayLike<number>, width: number, bounds: MascotRect): number {
  let clear = 0;
  for (let y = bounds.y; y < bounds.y + bounds.height; y += 1) {
    for (let x = bounds.x; x < bounds.x + bounds.width; x += 1) {
      if (alpha[y * width + x] === 0) clear += 1;
    }
  }
  return clear / (bounds.width * bounds.height);
}

/** The alpha plane (one byte a pixel) of RGBA pixel data such as ImageData.data. */
export function alphaPlaneOf(rgba: ArrayLike<number>): Uint8Array {
  const alpha = new Uint8Array(Math.floor(rgba.length / 4));
  for (let i = 0; i < alpha.length; i += 1) alpha[i] = rgba[i * 4 + 3];
  return alpha;
}

export interface AlphaFindings {
  /** Every pixel that catches clicks on the desktop (alpha 1 or more); null when nothing is visible. */
  bounds: MascotRect | null;
  transparentShare: number;
  /** Faint pixels (alpha 1 to 15) push the clickable box more than MASCOT_HAZE_TOLERANCE_PX past the art. */
  haze: boolean;
}

export function analyzeAlpha(alpha: ArrayLike<number>, width: number, height: number): AlphaFindings {
  const bounds = opaqueBounds(alpha, width, height);
  if (!bounds) return { bounds: null, transparentShare: 1, haze: false };
  const solid = opaqueBounds(alpha, width, height, MASCOT_HAZE_ALPHA);
  const haze = !solid
    || solid.x - bounds.x > MASCOT_HAZE_TOLERANCE_PX
    || solid.y - bounds.y > MASCOT_HAZE_TOLERANCE_PX
    || (bounds.x + bounds.width) - (solid.x + solid.width) > MASCOT_HAZE_TOLERANCE_PX
    || (bounds.y + bounds.height) - (solid.y + solid.height) > MASCOT_HAZE_TOLERANCE_PX;
  return { bounds, transparentShare: transparentShare(alpha, width, bounds), haze };
}

/** The pixel rules (the service's sentences): something visible, and a real cut-out. */
export function pixelRefusal(findings: AlphaFindings): MascotRefusal | null {
  if (!findings.bounds) return refuse('no_visible_pixels', 'The image is completely transparent, so desktops would show nothing. Check the export.');
  if (findings.transparentShare < MASCOT_LIMITS.minCutout) {
    return refuse('no_transparency', `Only ${Math.floor(findings.transparentShare * 1000) / 10}% of the box around the visible pixels is fully transparent; at least ${MASCOT_LIMITS.minCutout * 100}% must be. Desktops treat every visible pixel as clickable, so a solid block would catch clicks meant for the windows behind it. Use a cut-out on a transparent background.`);
  }
  return null;
}

const CHUNK_WORDS: Record<string, string> = {
  iCCP: 'colour profile (iCCP)',
  cHRM: 'colour primaries (cHRM)',
  tEXt: 'text (tEXt)',
  zTXt: 'compressed text (zTXt)',
  iTXt: 'text (iTXt)',
  eXIf: 'EXIF data (eXIf)',
  tIME: 'timestamp (tIME)',
  bKGD: 'background colour (bKGD)',
  tRNS: 'transparency key (tRNS)',
};

/** What the admin should know but that does not stop a publish. */
export function mascotWarnings(header: MascotPngHeader | null, findings: AlphaFindings | null): string[] {
  const warnings: string[] = [];
  const bounds = findings?.bounds;
  if (bounds && Math.max(bounds.width, bounds.height) < MASCOT_WARN_LONG_SIDE) {
    warnings.push(`After trimming the transparent border he is ${bounds.width} x ${bounds.height} pixels. Under ${MASCOT_WARN_LONG_SIDE} pixels on the longer side he looks soft at the largest mascot size; about 1024 pixels with a small transparent border works best.`);
  }
  if (findings?.haze) {
    warnings.push('Faint, nearly invisible pixels (alpha 1 to 15) make the area that catches clicks larger than the art. Turn on “Clickable area” to see it, and clean up the edges or soft shadow in the drawing.');
  }
  if (header && header.removed.length > 0) {
    const names = header.removed.map((type) => CHUNK_WORDS[type] ?? `the ${type} chunk`).join(', ');
    warnings.push(`These parts of the file are removed before it is stored: ${names}.${header.removed.includes('iCCP') ? ' Desktops ignore colour profiles, so colours can look slightly different there.' : ''}`);
  }
  return warnings;
}

// ---- Prepare for desktops (the pure steps; the card does the canvas work) ----

/**
 * Whether the card prepares a chosen file by itself: when the admin asked for a mirror, or when a readable PNG breaks
 * the byte limit or is wider or taller than the limit. A file that is not a PNG is refused as it is.
 */
export function needsPreparation(check: MascotPngCheck, mirror: boolean): boolean {
  return check.header !== null && (mirror || check.overLimits);
}

/** Step 1: mirror RGBA pixels left to right, in place. */
export function mirrorRgba(rgba: Uint8Array | Uint8ClampedArray, width: number, height: number): void {
  for (let y = 0; y < height; y += 1) {
    for (let left = 0, right = width - 1; left < right; left += 1, right -= 1) {
      const a = (y * width + left) * 4;
      const b = (y * width + right) * 4;
      for (let channel = 0; channel < 4; channel += 1) {
        const kept = rgba[a + channel];
        rgba[a + channel] = rgba[b + channel];
        rgba[b + channel] = kept;
      }
    }
  }
}

/**
 * Step 2, in place: alpha at or below 15 becomes 0 (haze that catches clicks unseen) and alpha at or above 240 becomes
 * 255 (a body exported at 253), and every fully transparent pixel gets RGB 0 so the file compresses well.
 */
export function cleanAlpha(rgba: Uint8Array | Uint8ClampedArray): void {
  for (let i = 0; i < rgba.length; i += 4) {
    const alpha = rgba[i + 3];
    if (alpha <= PREPARE.hazeAlpha) {
      rgba[i] = 0;
      rgba[i + 1] = 0;
      rgba[i + 2] = 0;
      rgba[i + 3] = 0;
    } else if (alpha >= PREPARE.solidAlpha) {
      rgba[i + 3] = 255;
    }
  }
}

/**
 * Step 3: the crop around the visible art plus a transparent margin, in the coordinates of a copy of the image padded by
 * `margin` on every side (so the crop never leaves that copy, even where the art touches the image edge).
 */
export function marginCrop(bounds: MascotRect, margin: number = PREPARE.marginPx): MascotRect {
  return { x: bounds.x, y: bounds.y, width: bounds.width + 2 * margin, height: bounds.height + 2 * margin };
}

/** Step 4: fit within the side limit (never enlarged). */
export function fitWithinLimit(size: MascotSize, maxSide: number = MASCOT_LIMITS.maxSide): MascotSize {
  return Math.max(size.width, size.height) <= maxSide ? { width: size.width, height: size.height } : fitInSquare(maxSide, size.width, size.height);
}

export type MascotEncoder<T> = (size: MascotSize) => Promise<{ result: T; bytes: number }>;

export type EncodedWithinLimit<T> =
  | { ok: true; result: T; bytes: number; size: MascotSize; attempts: number }
  | { ok: false; message: string; attempts: number };

/**
 * Steps 4 to 6 over a supplied encoder: encode the art fitted within the side limit; while the result is over the byte
 * limit, shrink the longer side by about 6% and encode again, down to the 512 px floor (always tried once); past the
 * floor, refuse. Each size is fitted from the original art, so rounding never drifts its shape.
 */
export async function encodeWithinLimit<T>(art: MascotSize, encode: MascotEncoder<T>): Promise<EncodedWithinLimit<T>> {
  let longer = Math.min(MASCOT_LIMITS.maxSide, Math.max(art.width, art.height));
  let attempts = 0;
  for (;;) {
    const size = fitWithinLimit(art, longer);
    attempts += 1;
    const encoded = await encode(size);
    if (encoded.bytes <= MASCOT_LIMITS.maxBytes) return { ok: true, result: encoded.result, bytes: encoded.bytes, size, attempts };
    if (longer <= PREPARE.floorPx) {
      return {
        ok: false,
        attempts,
        message: `Even at ${size.width} x ${size.height} pixels the image is ${formatBytes(encoded.bytes)}, more than the ${count(MASCOT_LIMITS.maxBytes)} bytes a desktop mascot may be. Simplify the art (fewer gradients and fine textures) and choose it again.`,
      };
    }
    longer = Math.max(PREPARE.floorPx, Math.round(longer * PREPARE.shrinkFactor));
  }
}

/** 756166 → "756 KB", 1432110 → "1.4 MB" (decimal units, as file managers show them). */
export function formatBytes(bytes: number): string {
  const kilobytes = Math.round(bytes / 1000);
  return kilobytes >= 1000 ? `${(bytes / 1_000_000).toFixed(1)} MB` : `${kilobytes} KB`;
}

export interface PreparedSummary {
  mirrored: boolean;
  trimmed: boolean;
  resized: boolean;
  from: MascotSize & { bytes: number };
  to: MascotSize & { bytes: number };
}

/** The one line under the preview: "Prepared: mirrored, trimmed, resized from 1254x1254 (1.4 MB) to 900x738 (756 KB)". */
export function preparedLine(summary: PreparedSummary): string {
  const steps = [summary.mirrored && 'mirrored', summary.trimmed && 'trimmed', summary.resized && 'resized'].filter(Boolean);
  return `Prepared: ${steps.length > 0 ? steps.join(', ') : 're-encoded'} from ${summary.from.width}x${summary.from.height} (${formatBytes(summary.from.bytes)}) `
    + `to ${summary.to.width}x${summary.to.height} (${formatBytes(summary.to.bytes)})`;
}

// ---- Bytes ----

/** Base64 of the exact bytes chosen, built in chunks so a large file never overflows the argument list. */
export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/** sha256 of the bytes, lowercase hex (Web Crypto). */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(bytes));
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('');
}

// ---- Yearly windows (the rules of services/gyro-svc/src/desktopMascotWindow.ts, cross-tested against it) ----

/**
 * The company's time zone (gyro-svc MASCOT_TIME_ZONE): a yearly window's months begin and end at midnight here, whatever
 * the time zone of the browser or of a PC.
 */
export const MASCOT_TIME_ZONE = 'America/Los_Angeles';
/** Month names, January first: the wording, and each toggle's accessible name. */
export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
/** The month toggles under "Every year in…", January first. */
export const MONTH_SHORT_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/** A yearly window's months as the service takes them: 1 (January) to 12 (December), at least one, each once, in calendar order. */
export function isMascotMonths(value: unknown): value is number[] {
  return Array.isArray(value) && value.length >= 1 && value.length <= 12
    && value.every((month, index) => Number.isInteger(month) && month >= 1 && month <= 12 && (index === 0 || month > value[index - 1]));
}

const PACIFIC = new Intl.DateTimeFormat('en-US', {
  timeZone: MASCOT_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
});

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** The calendar date and wall-clock time in the company's time zone at an instant. */
function pacificClock(ms: number): WallClock {
  const clock: WallClock = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  for (const part of PACIFIC.formatToParts(ms)) {
    if (Object.prototype.hasOwnProperty.call(clock, part.type)) clock[part.type as keyof WallClock] = Number(part.value);
  }
  clock.hour %= 24; // an engine that writes midnight as 24
  return clock;
}

/** A calendar date in the company's time zone: the year, the month (1 to 12) and the day. */
export interface PacificDate {
  year: number;
  month: number;
  day: number;
}

/** The calendar date in Pacific time at an instant. */
export function pacificDate(ms: number): PacificDate {
  const { year, month, day } = pacificClock(ms);
  return { year, month, day };
}

/** The month and day in Pacific time at an instant, "MM-DD": the date the outfit categories' seasons are judged on (lib/outfitCategories.ts). */
export function pacificMonthDay(ms: number): string {
  const { month, day } = pacificClock(ms);
  return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** How far the company's wall clock is from UTC at an instant, in ms: -7 hours in summer (PDT), -8 in winter (PST). */
function pacificOffsetMs(ms: number): number {
  const clock = pacificClock(ms);
  return Date.UTC(clock.year, clock.month - 1, clock.day, clock.hour, clock.minute, clock.second) - Math.floor(ms / 1000) * 1000;
}

const monthStarts = new Map<number, number>();

/** Midnight Pacific time at the start of a month's 1st, as epoch ms; `index` counts months: year * 12 + (month - 1). */
function monthStartMs(index: number): number {
  const known = monthStarts.get(index);
  if (known !== undefined) return known;
  const year = Math.floor(index / 12);
  const wall = Date.UTC(year, index - year * 12, 1);
  // The wall time read as UTC, moved by the zone's offset; the offset is read again at that guess, in case the first
  // reading fell on the other side of a daylight-saving change.
  const guess = wall - pacificOffsetMs(wall);
  const start = wall - pacificOffsetMs(guess);
  if (monthStarts.size >= 240) monthStarts.clear();
  monthStarts.set(index, start);
  return start;
}

/**
 * What desktops receive for a window at `now`, as the service works it out: always and between windows as they are; a
 * yearly window as its run of chosen months going on now, else the next run, from midnight Pacific time on the 1st of
 * its first month to midnight on the 1st of the month after its last (months next to each other run together, December
 * into January too), so it is in the gallery now or later, never over. All twelve months is always.
 */
export function effectiveWindow(galleryWindow: MascotWindow, now: number): OccurrenceWindow {
  if (galleryWindow.kind !== 'yearly') return galleryWindow;
  const chosen = new Set(galleryWindow.months.filter((month) => Number.isInteger(month) && month >= 1 && month <= 12));
  if (chosen.size === 0) throw new Error('desktop_mascot_yearly_window_without_months');
  if (chosen.size === 12) return { kind: 'always' };
  const isChosen = (index: number) => chosen.has((index % 12) + 1);
  const today = pacificClock(now);
  let first = today.year * 12 + (today.month - 1);
  if (isChosen(first)) {
    while (isChosen(first - 1)) first -= 1;
  } else {
    while (!isChosen(first)) first += 1;
  }
  let after = first + 1;
  while (isChosen(after)) after += 1;
  return { kind: 'between', startMs: monthStartMs(first), endMs: monthStartMs(after) };
}

/** Months as the toggles leave them: whole months 1 to 12 only, each once, in calendar order. */
export function normalizeMonths(months: ReadonlyArray<number>): number[] {
  return [...new Set(months)].filter((month) => Number.isInteger(month) && month >= 1 && month <= 12).sort((a, b) => a - b);
}

/** A month toggle pressed: the month is chosen, or no longer chosen when it was. */
export function toggleMonth(months: ReadonlyArray<number>, month: number): number[] {
  return normalizeMonths(months.includes(month) ? months.filter((chosen) => chosen !== month) : [...months, month]);
}

/**
 * Whether a month toggle under "Every year in…" reads as pressed (aria-pressed and its style): only while that window is
 * the one chosen. The inputs keep the months while another window is chosen, but saving that window ignores them.
 */
export function monthPressed(inputs: WindowInputs, month: number): boolean {
  return inputs.kind === 'yearly' && (inputs.months ?? []).includes(month);
}

/**
 * The inputs after a month toggle is pressed. While "Every year in…" is chosen, the month is chosen or no longer chosen
 * (toggleMonth). While another window is chosen no month reads as pressed, so pressing one chooses "Every year in…" with
 * that month and the months kept from before: it never takes away a month that read as not pressed.
 */
export function pressMonth(inputs: WindowInputs, month: number): WindowInputs {
  const months = inputs.months ?? [];
  return { ...inputs, kind: 'yearly', months: inputs.kind === 'yearly' ? toggleMonth(months, month) : normalizeMonths([...months, month]) };
}

/**
 * The months in words, each run of months next to each other together (December into January too): "October",
 * "Nov–Dec", "Dec–Jan", "March and October", "Jan–Mar, June and December"; "month" for all twelve.
 */
export function yearlyMonthsText(months: ReadonlyArray<number>): string {
  const chosen = new Set(normalizeMonths(months));
  if (chosen.size === 12) return 'month';
  const next = (month: number) => (month % 12) + 1;
  const words: string[] = [];
  for (let month = 1; month <= 12; month += 1) {
    // A run starts at a chosen month whose previous month is not chosen.
    if (!chosen.has(month) || chosen.has(month === 1 ? 12 : month - 1)) continue;
    let last = month;
    while (chosen.has(next(last))) last = next(last);
    words.push(last === month ? MONTH_NAMES[month - 1] : `${MONTH_SHORT_NAMES[month - 1]}–${MONTH_SHORT_NAMES[last - 1]}`);
  }
  return words.length <= 1 ? words[0] ?? '' : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** A day of a yearly window as the card writes it: the calendar date in Pacific time ("Oct 1, 2026"), in the browser's locale. */
export function formatPacificDate(ms: number, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { timeZone: MASCOT_TIME_ZONE, month: 'short', day: 'numeric', year: 'numeric' }).format(ms);
}

const PACIFIC_YEAR = new Intl.DateTimeFormat('en-US', { timeZone: MASCOT_TIME_ZONE, year: 'numeric' });

/**
 * A day as the featured summary writes it: the calendar date in Pacific time, with the year only when it is not this
 * year there ("Nov 1", "Jan 1, 2027"), in the browser's locale.
 */
export function formatPacificDay(ms: number, now: number, locale?: string): string {
  const thisYear = PACIFIC_YEAR.format(ms) === PACIFIC_YEAR.format(now);
  return new Intl.DateTimeFormat(locale, { timeZone: MASCOT_TIME_ZONE, month: 'short', day: 'numeric', ...(thisYear ? {} : { year: 'numeric' as const }) }).format(ms);
}

// ---- The gallery window ----

/** A datetime-local value ("YYYY-MM-DDTHH:mm") for an epoch time, in the browser's time zone (as Activity.tsx does). */
export function localDateTimeValue(value: number): string {
  const date = new Date(value);
  return new Date(value - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/**
 * In the gallery when the window is always, or from its start (included) to its end (not included), as the service rules.
 * A yearly window is judged by its occurrence going on now or next, so it is in the gallery or scheduled, never ended.
 */
export function outfitState(galleryWindow: MascotWindow, now: number): OutfitState {
  const shown = effectiveWindow(galleryWindow, now);
  if (shown.kind === 'always') return 'in-gallery';
  if (now < shown.startMs) return 'scheduled';
  if (now >= shown.endMs) return 'ended';
  return 'in-gallery';
}

export function sameWindow(left: MascotWindow, right: MascotWindow): boolean {
  if (left.kind === 'between' && right.kind === 'between') return left.startMs === right.startMs && left.endMs === right.endMs;
  if (left.kind === 'yearly' && right.kind === 'yearly') {
    return left.months.length === right.months.length && left.months.every((month, index) => month === right.months[index]);
  }
  return left.kind === 'always' && right.kind === 'always';
}

/**
 * Why the service would refuse a window when it is set or changed, or null: a start before the end, the end 5 minutes to
 * 366 days from now, and the start at most 366 days ago. "Always" is never refused; "Every year in…" needs only a month,
 * since it never ends.
 */
export function windowRefusal(galleryWindow: MascotWindow, now: number): string | null {
  if (galleryWindow.kind === 'always') return null;
  if (galleryWindow.kind === 'yearly') {
    return isMascotMonths(galleryWindow.months) ? null : 'Choose at least one month for “Every year in…”, or choose another gallery window.';
  }
  const { startMs, endMs } = galleryWindow;
  const days = MASCOT_LIMITS.maxShowUntilDays;
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return 'Choose a start and an end date and time for the gallery window, or choose “Always in the gallery”.';
  }
  if (startMs >= endMs) return 'The gallery window must start before it ends.';
  if (endMs < now + MASCOT_LIMITS.minShowUntilLeadMs) return 'The gallery window must end at least 5 minutes from now.';
  if (endMs > now + days * DAY_MS) return `The gallery window must end at most ${days} days from now.`;
  if (startMs < now - days * DAY_MS) return `The gallery window may start at most ${days} days ago.`;
  return null;
}

/** The calendar month `offsetMonths` after now's month (0: this month), in the browser's time zone: day 1 at 00:00 to the next month's. */
export function monthWindow(now: number, offsetMonths: number): BetweenWindow {
  const date = new Date(now);
  return {
    kind: 'between',
    startMs: new Date(date.getFullYear(), date.getMonth() + offsetMonths, 1).getTime(),
    endMs: new Date(date.getFullYear(), date.getMonth() + offsetMonths + 1, 1).getTime(),
  };
}

/**
 * The window as the card's inputs hold it: a choice, two datetime-local values in the browser's time zone, and the
 * months "Every year in…" names.
 */
export interface WindowInputs {
  kind: 'always' | 'between' | 'yearly';
  start: string;
  end: string;
  /** 1 (January) to 12 (December); absent until a month is chosen, and kept while another window is chosen. */
  months?: number[];
}

/** A new outfit's window: always, with "Only between" ready from now to the first day of next month at 00:00. */
export function defaultWindowInputs(now: number): WindowInputs {
  return { kind: 'always', start: localDateTimeValue(now), end: localDateTimeValue(monthWindow(now, 0).endMs) };
}

/** The inputs for a stored window (an always or yearly window keeps the new-outfit dates ready). */
export function windowInputsOf(galleryWindow: MascotWindow, now: number): WindowInputs {
  if (galleryWindow.kind === 'always') return defaultWindowInputs(now);
  if (galleryWindow.kind === 'yearly') return { ...defaultWindowInputs(now), kind: 'yearly', months: [...galleryWindow.months] };
  return { kind: 'between', start: localDateTimeValue(galleryWindow.startMs), end: localDateTimeValue(galleryWindow.endMs) };
}

/** "This month" and "Next month": the calendar month's boundaries as inputs. */
export function monthInputs(now: number, offsetMonths: number): WindowInputs {
  const month = monthWindow(now, offsetMonths);
  return { kind: 'between', start: localDateTimeValue(month.startMs), end: localDateTimeValue(month.endMs) };
}

/**
 * The window the inputs describe. A date left as it was loaded keeps the stored time to the millisecond (the inputs show
 * minutes), so an untouched window reads as unchanged; an empty or unreadable value becomes NaN, which windowRefusal names.
 * "Every year in…" sends its months in calendar order, each once (none chosen: windowRefusal names that too).
 */
export function windowFromInputs(inputs: WindowInputs, stored?: MascotWindow): MascotWindow {
  if (inputs.kind === 'always') return { kind: 'always' };
  if (inputs.kind === 'yearly') return { kind: 'yearly', months: normalizeMonths(inputs.months ?? []) };
  const before = stored?.kind === 'between' ? stored : null;
  const read = (value: string, storedMs: number | undefined): number =>
    storedMs !== undefined && value === localDateTimeValue(storedMs) ? storedMs : value ? Date.parse(value) : Number.NaN;
  return { kind: 'between', startMs: read(inputs.start, before?.startMs), endMs: read(inputs.end, before?.endMs) };
}

const MINUTE_MS = 60_000;

/** The datetime-local inputs' min and max (whole minutes inside the service's range). */
export function windowInputBounds(now: number): { startMin: string; endMin: string; max: string } {
  const ceilMinute = (value: number) => Math.ceil(value / MINUTE_MS) * MINUTE_MS;
  return {
    startMin: localDateTimeValue(ceilMinute(now - MASCOT_LIMITS.maxShowUntilDays * DAY_MS)),
    endMin: localDateTimeValue(ceilMinute(now + MASCOT_LIMITS.minShowUntilLeadMs)),
    max: localDateTimeValue(now + MASCOT_LIMITS.maxShowUntilDays * DAY_MS),
  };
}

// ---- Names, and what Add and Edit send ----

/** Why the service would refuse a name, or null: 1 to 40 characters after trimming, no control characters. */
export function nameRefusal(name: string): string | null {
  const trimmed = name.trim();
  if (trimmed.length === 0) return 'Give the outfit a name; people see it in their gallery.';
  if (trimmed.length > MASCOT_LIMITS.nameMax) return `The name may be at most ${MASCOT_LIMITS.nameMax} characters; this one has ${trimmed.length}.`;
  const control = [...trimmed].some((character) => {
    const code = character.charCodeAt(0);
    return code < 0x20 || code === 0x7f;
  });
  if (control) return 'The name must not contain control characters such as tabs or line breaks.';
  return null;
}

export type OutfitAdd = { kind: 'refused'; message: string } | { kind: 'ready'; body: AddMascotOutfitBody };

/** The POST body for "Add to the gallery", or why it would be refused (the name first, then the window). */
export function outfitAdd(
  input: { pngBase64: string; name: string; window: WindowInputs; featured: boolean; category?: string | null },
  expectedRevision: number,
  now: number,
): OutfitAdd {
  const named = nameRefusal(input.name);
  if (named) return { kind: 'refused', message: named };
  const galleryWindow = windowFromInputs(input.window);
  const timed = windowRefusal(galleryWindow, now);
  if (timed) return { kind: 'refused', message: timed };
  // The category by its id; General (null, '' or 'general') is no field at all, as a service before categories takes it.
  const category = input.category && input.category !== 'general' ? input.category : undefined;
  return {
    kind: 'ready',
    body: { pngBase64: input.pngBase64, name: input.name.trim(), window: galleryWindow, ...(input.featured ? { featured: true } : {}), ...(category ? { category } : {}), expectedRevision },
  };
}

/** The PATCH body that moves an outfit to a category (its id), or to General (null). */
export function categoryBody(category: string | null, expectedRevision: number): UpdateMascotOutfitBody {
  return { category: category && category !== 'general' ? category : null, expectedRevision };
}

export type OutfitEdit = { kind: 'unchanged' } | { kind: 'refused'; message: string } | { kind: 'ready'; body: UpdateMascotOutfitBody };

/**
 * The PATCH body for an inline edit, with only what changed: a renamed outfit sends its name, a moved window sends the
 * window (checked as the service checks a window that is set or changed). An ended outfit can be renamed as it is.
 */
export function outfitEdit(
  outfit: Pick<AdminOutfit, 'name' | 'window'>,
  input: { name: string; window: WindowInputs },
  expectedRevision: number,
  now: number,
): OutfitEdit {
  const body: UpdateMascotOutfitBody = { expectedRevision };
  const name = input.name.trim();
  if (name !== outfit.name) {
    const refusal = nameRefusal(input.name);
    if (refusal) return { kind: 'refused', message: refusal };
    body.name = name;
  }
  const galleryWindow = windowFromInputs(input.window, outfit.window);
  if (!sameWindow(galleryWindow, outfit.window)) {
    const refusal = windowRefusal(galleryWindow, now);
    if (refusal) return { kind: 'refused', message: refusal };
    body.window = galleryWindow;
  }
  return body.name === undefined && body.window === undefined ? { kind: 'unchanged' } : { kind: 'ready', body };
}

/**
 * The PATCH body for an outfit's Featured toggle: featured when it is not, not when it is. Every other featured outfit
 * stays featured (PUT /featured, which featured one outfit alone, is not used by the card any more).
 */
export function featureToggle(outfit: Pick<AdminOutfit, 'featured'>, expectedRevision: number): UpdateMascotOutfitBody {
  return { featured: !outfit.featured, expectedRevision };
}

export interface OutfitGroup {
  /** "always", "<startMs>-<endMs>", or "yearly:<months>" ("yearly:10" for every October). */
  key: string;
  window: MascotWindow;
  state: OutfitState;
  /** Catalog order. */
  outfits: AdminOutfit[];
}

const STATE_ORDER: Record<OutfitState, number> = { 'in-gallery': 0, scheduled: 1, ended: 2 };

/**
 * The catalog grouped by identical window, so a season of outfits sharing one window (say 13 for October) reads as one
 * block: the always group first, then what is in the gallery now, then scheduled, then ended, each by its start (a
 * yearly group by the start of its occurrence going on now or next).
 */
/** A window's group key: "always", "yearly:<months>" ("yearly:10" for every October) or "<startMs>-<endMs>". */
export function windowKey(span: MascotWindow): string {
  return span.kind === 'always' ? 'always' : span.kind === 'yearly' ? `yearly:${span.months.join(',')}` : `${span.startMs}-${span.endMs}`;
}

export function outfitGroups(outfits: ReadonlyArray<AdminOutfit>, now: number): OutfitGroup[] {
  const groups = new Map<string, OutfitGroup>();
  for (const outfit of outfits) {
    const span = outfit.window;
    const key = windowKey(span);
    const group = groups.get(key);
    if (group) group.outfits.push(outfit);
    else groups.set(key, { key, window: span, state: outfitState(span, now), outfits: [outfit] });
  }
  const startOf = ({ window: span }: OutfitGroup) => {
    const shown = effectiveWindow(span, now);
    return shown.kind === 'always' ? Number.NEGATIVE_INFINITY : shown.startMs;
  };
  const alwaysFirst = (group: OutfitGroup) => (group.key === 'always' ? 0 : 1);
  return [...groups.values()].sort((a, b) => alwaysFirst(a) - alwaysFirst(b) || STATE_ORDER[a.state] - STATE_ORDER[b.state] || startOf(a) - startOf(b) || 0);
}

/** The outfit already stored with exactly these bytes, if any (the service refuses a second copy as mascot_duplicate). */
export function duplicateOf(sha256: string, outfits: ReadonlyArray<Pick<AdminOutfit, 'id' | 'name' | 'sha256'>>): Pick<AdminOutfit, 'id' | 'name'> | null {
  return outfits.find((outfit) => outfit.sha256 === sha256) ?? null;
}

/**
 * The outfit kept outside the catalog with exactly these bytes, if any: a retired one, or one that never reached the
 * gallery. The service refuses the image again as mascot_duplicate, since it is that outfit: Restore brings it back.
 */
export function keptDuplicateOf(
  sha256: string,
  catalog: Partial<Pick<DesktopMascotCatalog, 'retired' | 'orphans'>>,
): { outfit: KeptOutfit; retired: boolean } | null {
  const retired = (catalog.retired ?? []).find((outfit) => outfit.sha256 === sha256);
  if (retired) return { outfit: retired, retired: true };
  const orphan = (catalog.orphans ?? []).find((outfit) => outfit.sha256 === sha256);
  return orphan ? { outfit: orphan, retired: false } : null;
}

/** A catalog and whatever the service told the card about day outfits and retired ones (absent from an older service). */
export type MascotCatalogLike = Pick<DesktopMascotCatalog, 'outfits'> & Partial<Pick<DesktopMascotCatalog, 'days' | 'retired' | 'dayWarnings'>>;

/**
 * The catalog outfits the gallery grid, its header and the featured line are about: every one but the day outfits (the
 * Day outfits section lists them; their catalog window is only the day an older gyro-svc shows them on), any other
 * outfit with a day rule (one from a newer release, hidden here), and the copy of a retired outfit a retire that did not
 * finish left in the catalog (people never see it). Catalog order.
 */
/** The tile grid's Animation filter (owner, 2026-10-19: "filter by if it has animation"): every outfit, only those with an animation, only those without. */
export type AnimationFilter = 'all' | 'animated' | 'still';

export const ANIMATION_FILTER_LABELS: Readonly<Record<AnimationFilter, string>> = { all: 'All', animated: 'Animated', still: 'Not animated' };

/**
 * Whether the catalog says which outfits have an animation (`hasActions` on any outfit, or the built-in Gyro's): then
 * the Animation filter is offered. A service with mascot animations off, or from before the filter, says nothing, and
 * every outfit would read as not animated, so the filter stays hidden.
 */
export function animationFilterAvailable(catalog: Pick<DesktopMascotCatalog, 'outfits' | 'builtInHasActions'>): boolean {
  return catalog.builtInHasActions !== undefined || catalog.outfits.some((outfit) => outfit.hasActions !== undefined);
}

/** Whether a tile with this `hasActions` passes the filter: Animated keeps true, Not animated everything else (an outfit the service did not mark included), All every tile. */
export function animationMatches(filter: AnimationFilter, hasActions: boolean | undefined): boolean {
  return filter === 'all' || (filter === 'animated') === (hasActions === true);
}

export function galleryOutfits(catalog: MascotCatalogLike): AdminOutfit[] {
  const days = catalog.days ?? {};
  const hidden = new Set([
    ...(catalog.retired ?? []).map((outfit) => outfit.id),
    ...(catalog.dayWarnings ?? []).flatMap((warning) => (warning.kind === 'newer-rule' ? [warning.outfitId] : [])),
  ]);
  return catalog.outfits.filter((outfit) => !Object.prototype.hasOwnProperty.call(days, outfit.id) && !hidden.has(outfit.id)
    && outfit.kind !== 'holiday' && outfit.kind !== 'fun');
}

/** The limits in force: the service's when it sent them, else this console's own (MASCOT_LIMITS), with the use unknown. */
export function mascotLimitsOf(catalog: Pick<DesktopMascotCatalog, 'limits'>): MascotLimitsView {
  return {
    catalog: catalog.limits?.catalog ?? MASCOT_LIMITS.maxOutfits,
    total: catalog.limits?.total ?? MASCOT_LIMITS.maxTotalOutfits,
    used: catalog.limits?.used ?? null,
  };
}

/**
 * Whether Add is closed, and why, in the card's words: Gyro keeps all the outfits it can, retired ones included
 * (retiring makes none), or the gallery holds all it can at once (an older service's catalog of 100, where retiring an
 * outfit makes room); else null. A service that keeps outfits past its catalog's 100 beyond it says the gallery holds
 * 2,000 at once, so only the total closes Add there.
 */
export function fullText(catalog: Pick<DesktopMascotCatalog, 'outfits' | 'limits'>): string | null {
  const limits = mascotLimitsOf(catalog);
  if (limits.used !== null && limits.used >= limits.total) {
    return `Gyro keeps ${count(limits.total)} outfits, the most it can, counting retired ones: they are kept for the costume shop, so retiring an outfit does not make room. No outfit can be added.`;
  }
  if (catalog.outfits.length >= limits.catalog) {
    return `The catalog holds ${count(limits.catalog)} outfits, the most it can at once. Retire one before adding another.`;
  }
  return null;
}

/**
 * The count over the grid: "33 shown · 74 in the gallery, day outfits included · 80 of 2000 kept, retired ones included".
 * `shown`: how many outfits the grid shows (galleryOutfits), said only when the gallery holds others (its day outfits,
 * listed in their own section); the last part only when the service said.
 */
export function catalogCountText(catalog: Pick<DesktopMascotCatalog, 'outfits' | 'limits'>, shown: number = catalog.outfits.length): string {
  const limits = mascotLimitsOf(catalog);
  const kept = limits.used === null ? '' : ` · ${count(limits.used)} of ${count(limits.total)} kept, retired ones included`;
  if (shown === catalog.outfits.length) return `${count(catalog.outfits.length)} in the gallery${kept}`;
  return `${count(shown)} shown · ${count(catalog.outfits.length)} in the gallery, day outfits included${kept}`;
}

// ---- Wording ----

export interface MascotTimeFormat {
  timeZone: string;
  /** How a time is written; the card uses the browser's locale. */
  format?: (ms: number) => string;
  /** How a yearly window's days are written (it always starts and ends at midnight Pacific time); the card uses formatPacificDate. */
  formatDate?: (ms: number) => string;
  /** The locale of the days in the featured summary (formatPacificDay); the card leaves it to the browser. */
  locale?: string;
}

/** A time as the card writes it, in the browser's locale and time zone. */
export function formatTime(ms: number): string {
  return new Date(ms).toLocaleString();
}

/** The name of the calendar month `offsetMonths` after now's ("October"), for the rotation buttons. */
export function monthName(now: number, offsetMonths: number): string {
  return new Date(monthWindow(now, offsetMonths).startMs).toLocaleString(undefined, { month: 'long' });
}

const defaultFormat = formatTime;
const quoted = (name: string): string => `“${name}”`;
/** What follows a yearly window's day in the notes: "every October, Pacific time". */
const yearlyNote = (months: ReadonlyArray<number>): string => `every ${yearlyMonthsText(months)}, Pacific time`;

/**
 * When an occurrence starts, as the notes write it: a time and the browser's time zone, or a yearly window's first day
 * ("Oct 1, 2027 (every October, Pacific time)").
 */
function startText(galleryWindow: MascotWindow, shown: BetweenWindow, options: MascotTimeFormat): string {
  if (galleryWindow.kind === 'yearly') return `${(options.formatDate ?? formatPacificDate)(shown.startMs)} (${yearlyNote(galleryWindow.months)})`;
  return `${(options.format ?? defaultFormat)(shown.startMs)} (${options.timeZone})`;
}

/**
 * "Always in the gallery", "In the gallery from <start> to <end> (<time zone>)", or a yearly window's months with the days
 * of its occurrence going on now, else the next one: "Every October (Pacific time): now Oct 1, 2026 through Oct 31,
 * 2026", "Every Nov–Dec (Pacific time): next Nov 1, 2026 through Dec 31, 2026".
 */
export function windowText(galleryWindow: MascotWindow, options: MascotTimeFormat, now: number = Date.now()): string {
  if (galleryWindow.kind === 'always') return 'Always in the gallery';
  if (galleryWindow.kind === 'yearly') {
    const shown = effectiveWindow(galleryWindow, now);
    if (shown.kind === 'always') return 'Every month: always in the gallery';
    const day = options.formatDate ?? formatPacificDate;
    return `Every ${yearlyMonthsText(galleryWindow.months)} (Pacific time): ${shown.startMs <= now ? 'now' : 'next'} ${day(shown.startMs)} through ${day(shown.endMs - 1)}`;
  }
  const at = options.format ?? defaultFormat;
  return `In the gallery from ${at(galleryWindow.startMs)} to ${at(galleryWindow.endMs)} (${options.timeZone})`;
}

/**
 * The line under "Every year in…": what is missing, or the chosen months and the days of their occurrence going on now,
 * else the next one ("Every October, by the calendar in Pacific time (America/Los_Angeles). Now: Oct 1, 2026 through
 * Oct 31, 2026.").
 */
export function yearlyHint(months: ReadonlyArray<number>, now: number, formatDate: (ms: number) => string = formatPacificDate): string {
  const chosen = normalizeMonths(months);
  if (chosen.length === 0) {
    return `Choose one or more months: the outfit is in the gallery in those months every year, by the calendar in Pacific time (${MASCOT_TIME_ZONE}).`;
  }
  const shown = effectiveWindow({ kind: 'yearly', months: chosen }, now);
  if (shown.kind === 'always') return 'Every month: always in the gallery, the same as “Always in the gallery”.';
  return `Every ${yearlyMonthsText(chosen)}, by the calendar in Pacific time (${MASCOT_TIME_ZONE}). `
    + `${shown.startMs <= now ? 'Now' : 'Next'}: ${formatDate(shown.startMs)} through ${formatDate(shown.endMs - 1)}.`;
}

/**
 * What people who have not picked an outfit get now: the first featured outfit, in catalog order, that is in the
 * gallery now, else null (the built-in Gyro). The service's rule (desktopMascotWindow.ts featuredInGallery), which skips
 * day outfits and retired ones (galleryOutfits).
 */
export function defaultLook(catalog: MascotCatalogLike, now: number): AdminOutfit | null {
  return galleryOutfits(catalog).find((outfit) => outfit.featured && outfitState(outfit.window, now) === 'in-gallery') ?? null;
}

/** How far ahead featuredTimeline looks: every window comes round within it (a dated one ends at most 366 days after it is set). */
export const FEATURED_TIMELINE_DAYS = 366;

/** One stretch of featuredTimeline: who people who have not picked an outfit get, and from when. */
export interface FeaturedSpan {
  /** The featured outfit they get, or null: the built-in Gyro. */
  outfit: AdminOutfit | null;
  /** When it starts: now, for the first. */
  fromMs: number;
  /**
   * How that start is written: 'now'; 'month' where a yearly window turns, at midnight Pacific time on a 1st (a day);
   * 'time' where a dated window starts or ends (a time in the browser's time zone).
   */
  from: 'now' | 'month' | 'time';
}

/**
 * The next moment after `time` that one of these outfits' windows starts or ends (+Infinity when none will), and
 * whether a yearly window turns then (midnight Pacific time on a 1st, which the card writes as a day).
 */
function nextWindowTurn(outfits: ReadonlyArray<Pick<AdminOutfit, 'window'>>, time: number): { time: number; month: boolean } {
  let next = Number.POSITIVE_INFINITY;
  let month = false;
  for (const outfit of outfits) {
    const shown = effectiveWindow(outfit.window, time);
    if (shown.kind !== 'between') continue;
    const turn = time < shown.startMs ? shown.startMs : time < shown.endMs ? shown.endMs : Number.POSITIVE_INFINITY;
    if (turn < next) {
      next = turn;
      month = outfit.window.kind === 'yearly';
    } else if (turn === next && outfit.window.kind === 'yearly') {
      month = true;
    }
  }
  return { time: next, month };
}

/**
 * Who people who have not picked an outfit get from now on (defaultLook at each moment) over the next
 * FEATURED_TIMELINE_DAYS, one span for each change. It can change only where a featured outfit's window starts or ends.
 */
export function featuredTimeline(outfits: ReadonlyArray<AdminOutfit>, now: number): FeaturedSpan[] {
  const featured = outfits.filter((outfit) => outfit.featured);
  const at = (time: number) => defaultLook({ outfits: featured }, time);
  const spans: FeaturedSpan[] = [{ outfit: at(now), fromMs: now, from: 'now' }];
  const horizon = now + FEATURED_TIMELINE_DAYS * DAY_MS;
  for (let turn = nextWindowTurn(featured, now); turn.time <= horizon; turn = nextWindowTurn(featured, turn.time)) {
    const outfit = at(turn.time);
    if ((outfit?.id ?? null) !== (spans[spans.length - 1].outfit?.id ?? null)) spans.push({ outfit, fromMs: turn.time, from: turn.month ? 'month' : 'time' });
  }
  return spans;
}

/**
 * Who people who have not picked an outfit get instead of `outfit`, one of the featured `outfits`, whenever it is in the
 * gallery over the next FEATURED_TIMELINE_DAYS: the featured outfits added before it that are in the gallery then, in
 * catalog order (defaultLook in each stretch between the moments a featured outfit's window starts or ends, its own
 * included). For a featured outfit they never get.
 */
function featuredAhead(outfit: Pick<AdminOutfit, 'id' | 'window'>, outfits: ReadonlyArray<AdminOutfit>, now: number): AdminOutfit[] {
  const featured = outfits.filter((item) => item.featured);
  const horizon = now + FEATURED_TIMELINE_DAYS * DAY_MS;
  const ahead = new Set<string>();
  for (let time = now; time <= horizon; time = nextWindowTurn(featured, time).time) {
    if (outfitState(outfit.window, time) !== 'in-gallery') continue;
    const shown = defaultLook({ outfits: featured }, time);
    if (shown && shown.id !== outfit.id) ahead.add(shown.id);
  }
  return featured.filter((item) => ahead.has(item.id));
}

/** At most this many changes ahead in the featured line; more read "and more after that". */
const FEATURED_LINE_CHANGES = 5;
/** At most this many names in a list of featured outfits nobody sees; more read "and N more". */
const UNSEEN_NAMES_MAX = 4;

/** When a span starts: a day where a yearly window turns ("Nov 1", optionally with "(Pacific time)"), else a time and time zone. */
function spanStartText(span: FeaturedSpan, now: number, options: MascotTimeFormat, pacific: boolean): string {
  if (span.from === 'month') return `${formatPacificDay(span.fromMs, now, options.locale)}${pacific ? ' (Pacific time)' : ''}`;
  return `${(options.format ?? defaultFormat)(span.fromMs)} (${options.timeZone})`;
}

/** “A”, “A” and “B”, “A”, “B” and “C”; past UNSEEN_NAMES_MAX, “A”, “B”, “C”, “D” and 2 more (“or” in place of “and” when asked). */
function namesText(names: ReadonlyArray<string>, conjunction: 'and' | 'or' = 'and'): string {
  const quotedNames = names.slice(0, UNSEEN_NAMES_MAX).map(quoted);
  if (names.length > UNSEEN_NAMES_MAX) return `${quotedNames.join(', ')} ${conjunction} ${names.length - UNSEEN_NAMES_MAX} more`;
  return quotedNames.length <= 1 ? quotedNames.join('') : `${quotedNames.slice(0, -1).join(', ')} ${conjunction} ${quotedNames[quotedNames.length - 1]}`;
}

/**
 * The header's first line: who people who have not picked an outfit see now and next (featuredTimeline), until it comes
 * back to someone already named or the year is over, as in "People who have not picked an outfit see “Mummy” now
 * (October), then “Pilgrim” from Nov 1 (Pacific time), then “Santa” from Dec 1, then the built-in Gyro from Jan 1, 2027,
 * then “Mummy” again from Oct 1, 2027." Featured outfits nobody sees in that year are named after it.
 */
export function featuredSummaryLine(outfits: ReadonlyArray<AdminOutfit>, now: number, options: MascotTimeFormat): string {
  const spans = featuredTimeline(outfits, now);
  const who = (outfit: AdminOutfit | null) => (outfit ? quoted(outfit.name) : 'the built-in Gyro');
  const first = spans[0];
  let line: string;
  if (spans.length === 1) {
    // Nothing changes in the coming year: an outfit people get all year is always in the gallery.
    line = first.outfit ? `People who have not picked an outfit see ${quoted(first.outfit.name)}, featured and always in the gallery.` : 'People who have not picked an outfit see the built-in Gyro.';
  } else {
    const shownWindow = first.outfit?.window;
    const months = shownWindow?.kind === 'yearly' && shownWindow.months.length < 12 ? ` (${yearlyMonthsText(shownWindow.months)})` : '';
    line = `People who have not picked an outfit see ${who(first.outfit)} now${months}`;
    const named = new Set([first.outfit?.id ?? null]);
    // "(Pacific time)" follows the first day only.
    let pacific = true;
    for (const [index, span] of spans.slice(1).entries()) {
      if (index === FEATURED_LINE_CHANGES) {
        line += ', and more after that';
        break;
      }
      const again = named.has(span.outfit?.id ?? null);
      line += `, then ${who(span.outfit)}${again ? ' again' : ''} from ${spanStartText(span, now, options, pacific && span.from === 'month')}`;
      if (span.from === 'month') pacific = false;
      if (again) break;
      named.add(span.outfit?.id ?? null);
    }
    line += '.';
  }
  const shownIds = new Set(spans.map((span) => span.outfit?.id ?? null));
  const unseen = outfits.filter((outfit) => outfit.featured && !shownIds.has(outfit.id));
  return unseen.length > 0 ? `${line} Featured but not shown in the coming year: ${namesText(unseen.map((outfit) => outfit.name))}.` : line;
}

/**
 * The card's header: who people who have not picked an outfit see now and next, then the day outfits (counted apart:
 * they come on their own days) and how many outfits Gyro keeps of the most it can (retired ones included; while the
 * service cannot say, the gallery's own count). Who can pick the gallery's outfits now is the visibility line
 * (lib/outfitVisibility.ts galleryVisibility), which the card puts first: a count by date window alone read as
 * "everyone can pick these" while most sets were switched off (owner, 2026-10-13).
 */
export function gallerySummaryLines(catalog: MascotCatalogLike & Pick<DesktopMascotCatalog, 'limits'>, now: number, options: MascotTimeFormat): string[] {
  const outfits = galleryOutfits(catalog);
  const days = Object.keys(catalog.days ?? {}).length;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days === 1 ? '1 day outfit comes on its own day' : `${days} day outfits come on their own days`} (Day outfits, below).`);
  const limits = mascotLimitsOf(catalog);
  parts.push(limits.used === null
    ? `${catalog.outfits.length} of ${count(limits.total)} outfits stored.`
    : `${count(limits.used)} of ${count(limits.total)} outfits stored, retired ones included.`);
  return [featuredSummaryLine(outfits, now, options), parts.join(' ')];
}

/**
 * The selected outfit's line about featuring, or null when it is not featured: whether people who have not picked an
 * outfit see it now, from when, or why they never do in the coming year: its window has ended (then nobody sees it), or
 * whenever it is in the gallery they get a featured outfit added before it instead, named here (people who picked it
 * still see it then).
 */
export function featuredStatusText(outfit: Pick<AdminOutfit, 'id' | 'featured' | 'window'>, outfits: ReadonlyArray<AdminOutfit>, now: number, options: MascotTimeFormat): string | null {
  if (!outfit.featured) return null;
  const span = featuredTimeline(outfits, now).find((candidate) => candidate.outfit?.id === outfit.id);
  if (span?.from === 'now') return 'Featured: people who have not picked an outfit see it now.';
  if (span) return `Featured: people who have not picked an outfit see it from ${spanStartText(span, now, options, true)}.`;
  if (outfitState(outfit.window, now) === 'ended') return 'Featured, but its window has ended, so nobody sees it until you change the window.';
  const never = 'Featured, but people who have not picked an outfit never get it in the coming year: whenever it is in the gallery,';
  const ahead = featuredAhead(outfit, outfits, now);
  if (ahead.length === 0) return `${never} so is a featured outfit added before it.`;
  return `${never} they get ${namesText(ahead.map((item) => item.name), 'or')} instead, ${ahead.length === 1 ? '' : 'each '}featured and added before it.`;
}

/** How many people stored this pick ("default" or an outfit id); 0 when the service sent none. */
export function picksFor(picks: Readonly<Record<string, number>>, look: string): number {
  const value = Object.prototype.hasOwnProperty.call(picks, look) ? picks[look] : 0;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

export function pickCountText(count: number): string {
  if (count <= 0) return 'Nobody picked it';
  return count === 1 ? '1 person picked it' : `${count.toLocaleString('en-US')} people picked it`;
}

/**
 * The outfit ids whose 'remove' history lines were retires: the retired outfits and those back in the catalog (an id is
 * never handed out twice, so one back in the catalog was restored). Any other 'remove' line is a deletion by a gyro-svc
 * from before Retire, or one whose outfit never reached the gallery again.
 */
export function retiredIdsOf(catalog: Pick<DesktopMascotCatalog, 'outfits'> & Partial<Pick<DesktopMascotCatalog, 'retired'>>): Set<string> {
  return new Set([...(catalog.retired ?? []), ...catalog.outfits].map((outfit) => outfit.id));
}

/**
 * One line of the history list, for every action (the last three are #286 entries the migration carried over). A
 * 'remove' line reads "retired" for an outfit in `retiredIds` (retiredIdsOf), else "removed". A line about an outfit in
 * `dayIds` (a day outfit) never names its catalog window: that is only the day an older gyro-svc would show it on, which
 * the service moves on by itself; its dates come from its day rule.
 */
export function historyLine(entry: MascotHistoryEntry, format: (ms: number) => string = defaultFormat, retiredIds: ReadonlySet<string> = new Set(), dayIds: ReadonlySet<string> = new Set()): string {
  const who = `${format(entry.atMs)}: ${entry.by}`;
  const named = entry.name ? quoted(entry.name) : entry.outfitId ? `outfit ${entry.outfitId}` : 'an outfit';
  const day = entry.outfitId !== null && dayIds.has(entry.outfitId);
  const stored = day ? null : entry.window;
  const span = !stored ? null
    : stored.kind === 'always' ? 'always in the gallery'
      : `in the gallery from ${format(stored.startMs)} to ${format(stored.endMs)}`;
  const until = stored?.kind === 'between' ? `, until ${format(stored.endMs)}` : '';
  switch (entry.action) {
    case 'add':
      return day ? `${who} added the day outfit ${named}` : `${who} added ${named}${span ? `, ${span}` : ''}`;
    case 'edit':
      return day ? `${who} renamed the day outfit ${named}` : `${who} edited ${named}${span ? ` (${span})` : ''}`;
    case 'remove':
      return `${who} ${entry.outfitId !== null && retiredIds.has(entry.outfitId) ? 'retired' : 'removed'} ${named}`;
    case 'feature':
      return `${who} featured ${named}`;
    case 'unfeature':
      // A line naming no outfit stopped several at once (PUT /featured with none); one naming one stopped that one.
      return entry.outfitId === null && entry.name === null ? `${who} stopped featuring every outfit` : `${who} stopped featuring ${named}`;
    case 'publish':
      return `${who} published ${entry.name ? quoted(entry.name) : 'a custom image'} to every desktop${until}`;
    case 'schedule':
      return `${who} changed the date or label${entry.name ? ` (${quoted(entry.name)})` : ''}${until}`;
    case 'reset':
      return `${who} reset desktops to the built-in Gyro`;
    default:
      return `${who} changed the gallery`;
  }
}

/** A line of the service's other history (AnnexHistoryLine), in History's words. */
export function annexHistoryText(line: AnnexHistoryLine, format: (ms: number) => string = defaultFormat): string {
  const who = `${format(line.atMs)}: ${line.by}`;
  switch (line.action) {
    case 'day':
      return `${who} changed the day outfit ${quoted(line.name)} (its day, greeting, kind or collection)`;
    case 'retire':
      return `${who} retired ${quoted(line.name)}`;
    case 'restore':
      return `${who} restored ${quoted(line.name)}`;
    // An outfit kept beyond the catalog (past its first 100): its changes are listed here, never in the catalog's lines.
    case 'overflow-add':
      return `${who} added ${quoted(line.name)}`;
    case 'overflow-edit':
      return line.change === 'feature' ? `${who} featured ${quoted(line.name)}`
        : line.change === 'unfeature' ? `${who} stopped featuring ${quoted(line.name)}`
          : `${who} edited ${quoted(line.name)}`;
    default:
      return `${who} changed ${quoted(line.name)}`;
  }
}

/**
 * The History list, newest first, as one list: the catalog's lines (historyLine) and the service's other history
 * (annexHistoryText), which holds what the catalog never records (a day outfit's new day or greeting, the retire of an
 * outfit that never reached the gallery, the restore of a retire that did not finish). A retire or restore of an outfit
 * in the catalog has a line in both, the annex's first: History shows it once, as "retired" or "restored" (a restore's
 * 'add' would otherwise read "added"). Each line has a key for React.
 */
export function galleryHistory(
  catalog: Pick<DesktopMascotCatalog, 'history' | 'outfits'> & Partial<Pick<DesktopMascotCatalog, 'annexHistory' | 'retired' | 'orphans' | 'days'>>,
  format: (ms: number) => string = defaultFormat,
): Array<{ key: string; text: string }> {
  const retiredIds = retiredIdsOf(catalog);
  const dayIds = new Set([
    ...Object.keys(catalog.days ?? {}),
    ...[...(catalog.retired ?? []), ...(catalog.orphans ?? [])].filter((outfit) => outfit.kind !== null).map((outfit) => outfit.id),
  ]);
  type Item = { annex: false; atMs: number; entry: MascotHistoryEntry; index: number } | { annex: true; atMs: number; line: AnnexHistoryLine; index: number };
  // Oldest first, an annex line before the catalog's at the same instant (its step comes first).
  const items: Item[] = [
    ...catalog.history.map((entry, index): Item => ({ annex: false, atMs: entry.atMs, entry, index })),
    ...(catalog.annexHistory ?? []).map((line, index): Item => ({ annex: true, atMs: line.atMs, line, index })),
  ].sort((a, b) => a.atMs - b.atMs || Number(b.annex) - Number(a.annex) || b.index - a.index);
  // The catalog line each annex line stands for, by outfit: the next 'remove' after a retire, the next 'add' after a restore.
  const pending = new Map<string, MascotHistoryAction>();
  const shown: Array<{ key: string; text: string }> = [];
  for (const item of items) {
    if (item.annex) {
      if (item.line.withCatalogLine && (item.line.action === 'retire' || item.line.action === 'restore')) pending.set(item.line.outfitId, item.line.action === 'retire' ? 'remove' : 'add');
      else pending.delete(item.line.outfitId);
      shown.push({ key: `annex-${item.atMs}-${item.line.action}-${item.index}`, text: annexHistoryText(item.line, format) });
      continue;
    }
    const { entry } = item;
    if (entry.outfitId !== null && pending.get(entry.outfitId) === entry.action) {
      pending.delete(entry.outfitId);
      continue;
    }
    shown.push({ key: `catalog-${item.atMs}-${entry.action}-${item.index}`, text: historyLine(entry, format, retiredIds, dayIds) });
  }
  return shown.reverse();
}

/**
 * A featured outfit's paragraph in the retire confirmation: it leaves the featured outfits (the others stay featured),
 * and the first change retiring it makes for people who have not picked an outfit in the coming year, or that nothing
 * changes for them. Without it they get something else exactly where they would have got it, so the first change is the
 * start of its first span in featuredTimeline, and there they get defaultLook without it instead.
 */
function featuredRetireText(outfit: Pick<AdminOutfit, 'id'>, outfits: ReadonlyArray<AdminOutfit>, now: number, options: MascotTimeFormat): string {
  const others = outfits.filter((item) => item.id !== outfit.id);
  const leaves = others.some((item) => item.featured)
    ? 'It is featured, so it leaves the featured outfits too; the others stay featured.'
    : 'It is the only featured outfit, so no outfit is featured afterwards.';
  const span = featuredTimeline(outfits, now).find((candidate) => candidate.outfit?.id === outfit.id);
  if (!span) return `${leaves} People who have not picked an outfit do not get it in the coming year anyway, so nothing changes for them.`;
  const instead = defaultLook({ outfits: others }, span.fromMs);
  const who = instead ? quoted(instead.name) : 'the built-in Gyro';
  return span.from === 'now'
    ? `${leaves} People who have not picked an outfit get ${who} now instead of it.`
    : `${leaves} From ${spanStartText(span, now, options, true)}, people who have not picked an outfit get ${who} instead of it.`;
}

/** The paragraphs every retire confirmation ends with: nothing is deleted, and the retire is audited. */
const RETIRE_KEEPS = 'Nothing is deleted: it is kept with its image and ratings under Retired, in Day outfits below, and Restore brings it back under the same id.\n\n'
  + 'The retire is recorded in the audit trail.';

/**
 * What a retire confirmation ends with when the service is a gyro-svc from before Retire (it sends no annexRevision, as
 * after a rollback while this console was open): its DELETE takes the outfit out of the catalog for good.
 */
const RETIRE_REMOVES = 'This service is an older version of Gyro that cannot keep retired outfits: the outfit leaves the catalog for good and cannot be restored. Adding its image again makes a new outfit.\n\n'
  + 'The change is recorded in the audit trail.';

/** Whether the service keeps retired outfits (Retire and Restore): it sends annexRevision (null while it cannot read them). */
export function serviceKeepsRetired(catalog: Partial<Pick<DesktopMascotCatalog, 'annexRevision'>>): boolean {
  return catalog.annexRevision !== undefined;
}

/**
 * The confirmation Retire asks for (window.confirm). People who picked the outfit go back to automatic: the first
 * featured outfit in the gallery now without it, else the built-in Gyro. A featured outfit leaves the featured outfits,
 * and the confirmation says from when people who have not picked an outfit get something else instead of it, such as
 * the built-in Gyro from Nov 1 in place of a November outfit (featuredRetireText). The outfit is kept, never deleted,
 * except by a gyro-svc from before Retire (`keeps` false: serviceKeepsRetired), which removes it for good.
 */
export function retireConfirmText(
  outfit: Pick<AdminOutfit, 'id' | 'name' | 'featured'>,
  catalog: MascotCatalogLike & Pick<DesktopMascotCatalog, 'picks'>,
  now: number,
  options: MascotTimeFormat & { keeps?: boolean },
): string {
  const outfits = galleryOutfits(catalog);
  const count = picksFor(catalog.picks, outfit.id);
  const other = defaultLook({ outfits: outfits.filter((item) => item.id !== outfit.id) }, now);
  const automatic = other ? `${quoted(other.name)} now (the first featured outfit in the gallery)` : 'the built-in Gyro now';
  const pickers = count === 0
    ? 'Nobody picked it.'
    : `${count === 1 ? '1 person picked it' : `${count.toLocaleString('en-US')} people picked it`}; they go back to automatic, which is ${automatic}. Their desktops switch on their next check, within about 20 minutes.`;
  const featuredLine = outfit.featured ? `\n\n${featuredRetireText(outfit, outfits, now, options)}` : '';
  return `Retire ${quoted(outfit.name)} from the gallery?\n\n${pickers}${featuredLine}\n\n${options.keeps === false ? RETIRE_REMOVES : RETIRE_KEEPS}`;
}

/**
 * The confirmation Retire asks for on a day outfit: it is no longer swapped in on its day, and people who picked it go
 * back to automatic.
 */
export function retireDayConfirmText(outfit: Pick<AdminOutfit, 'id' | 'name'>, picks: Readonly<Record<string, number>>): string {
  const count = picksFor(picks, outfit.id);
  const pickers = count === 0
    ? 'Nobody picked it.'
    : `${count === 1 ? '1 person picked it' : `${count.toLocaleString('en-US')} people picked it`}; they go back to automatic. Their desktops switch on their next check, within about 20 minutes.`;
  return `Retire the day outfit ${quoted(outfit.name)}?\n\nIt is no longer swapped in on its day, and its greeting no longer replaces “What can we work on?”. ${pickers}\n\n${RETIRE_KEEPS}`;
}

/**
 * The confirmation Retire asks for under "Needs attention": an outfit that never reached the gallery, a retired one whose
 * catalog copy a retire left behind (this finishes it), or one with a day rule from a newer release.
 */
export function attentionRetireConfirmText(
  outfit: { id: string; name: string; reason: 'orphan' | 'pending-tidy' | 'newer-rule' },
  picks: Readonly<Record<string, number>>,
): string {
  if (outfit.reason === 'pending-tidy') {
    return `Finish retiring ${quoted(outfit.name)}?\n\nIt is already retired; its leftover copy leaves the catalog. Nobody sees it now either way.\n\n${RETIRE_KEEPS}`;
  }
  if (outfit.reason === 'newer-rule') {
    const count = picksFor(picks, outfit.id);
    const pickers = count === 0 ? 'Nobody picked it.' : `${count === 1 ? '1 person' : `${count.toLocaleString('en-US')} people`} picked it; they stay on automatic.`;
    return `Retire ${quoted(outfit.name)}?\n\nIts day rule is from a newer release of Gyro, so nobody sees it on this one. ${pickers}\n\n${RETIRE_KEEPS}`;
  }
  return `Retire ${quoted(outfit.name)}?\n\nIt never reached the gallery, so nobody sees it now.\n\n${RETIRE_KEEPS}`;
}

/** The note after an outfit is added. */
export function addedText(added: DesktopMascotAdded, now: number, options: MascotTimeFormat): string {
  const outfit = added.outfits.find((item) => item.id === added.outfitId);
  const name = outfit ? quoted(outfit.name) : 'the outfit';
  const span = outfit?.window;
  const state = span ? outfitState(span, now) : null;
  const shown = span ? effectiveWindow(span, now) : null;
  const where = state === 'in-gallery' ? ' It is in the gallery now.'
    : state === 'scheduled' && span && shown?.kind === 'between' ? ` It joins the gallery on ${startText(span, shown, options)}.`
      : state === 'ended' ? ' Its window has already ended, so nobody sees it until you change the window.' : '';
  const featured = outfit?.featured
    ? ' It is featured: people who have not picked an outfit get it while it is the first featured outfit in the gallery.'
    : '';
  const stripped = added.strippedChunks.length > 0 ? ` Removed from the file before storing: ${added.strippedChunks.join(', ')}.` : '';
  return `Added ${name}.${where}${featured} The change is in the audit trail.${stripped}`;
}

/** The note after the Featured toggle; the outfit's own line (featuredStatusText) says when people see it. */
export function featuredText(outfit: Pick<AdminOutfit, 'name' | 'featured'>): string {
  return `${quoted(outfit.name)} ${outfit.featured ? 'is featured' : 'is no longer featured'}; the other featured outfits stay as they are. `
    + 'Desktops follow on their next check, within about 20 minutes. The change is in the audit trail.';
}

/** The note after Retire; `keeps` false: the service was a gyro-svc from before Retire (serviceKeepsRetired). */
export function retiredText(name: string, keeps = true): string {
  if (!keeps) {
    return `Removed ${quoted(name)} from the catalog: this older version of the service does not keep retired outfits, so it cannot be restored. `
      + 'People who picked it go back to automatic on their next check, within about 20 minutes. The change is in the audit trail.';
  }
  return `Retired ${quoted(name)}: it is kept under Retired, in Day outfits below, where Restore brings it back. `
    + 'People who picked it go back to automatic on their next check, within about 20 minutes. The retire is in the audit trail.';
}

export const MASCOT_SAVED_TEXT = 'Saved. The change is in the audit trail; desktops pick it up on their next check, within about 20 minutes.';
export const MASCOT_CHANGED_TEXT = 'Another admin changed the mascot gallery while you were working, so nothing was saved. The card now shows their change; try again if you still want yours.';
export const MASCOT_READ_ONLY_TEXT = 'You are signed in as an auditor: you can view the gallery and its history, but only IT admins can add, change, feature, retire or restore outfits.';

/**
 * A readable sentence for a 401 or 403 (the console's API helper turns those into the bare words "unauthenticated" and
 * "forbidden"), or null for any other error, which the card shows with errorText or mascotErrorText.
 */
export function mascotAuthText(error: unknown, action: 'read' | 'save'): string | null {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  if (status === 401) return 'Your admin session has ended. Sign in again, then retry.';
  if (status === 403) {
    return action === 'read'
      ? 'The mascot gallery is visible to IT admins and auditors only.'
      : 'Only IT admins can change the mascot gallery; auditors can view it.';
  }
  return null;
}

/** The service's error code ({"error": ...} in the body of an ApiError), or null. */
export function mascotErrorCode(error: unknown): string | null {
  const body = (error as { body?: unknown } | null | undefined)?.body;
  const code = body && typeof body === 'object' ? (body as Record<string, unknown>).error : undefined;
  return typeof code === 'string' ? code : null;
}

const RELOAD_CODES = [
  'mascot_changed',
  'mascot_outfit_not_found',
  'mascot_duplicate',
  'mascot_catalog_full',
  'mascot_audit_pending',
  'mascot_audit_failed',
  'mascot_not_retired',
  'mascot_import_exists',
  'mascot_import_orphaned',
  'mascot_not_day_outfit',
  'mascot_newer_rule',
  'mascot_day_outfit_window',
  'mascot_not_featurable',
  'mascot_retire_unfinished',
  'mascot_restore_unfinished',
];

/**
 * Whether the card reads the catalog again after this error: another admin's change won, the outfit or the image is
 * already gone or already there, the catalog filled up meanwhile, the change was saved with its audit record queued or
 * missing, the outfit is not what the card showed (already restored, a day outfit, or one a newer release changed), or a
 * retire or restore went part of the way (the outfit is under Needs attention now).
 */
export function mascotErrorReloads(error: unknown): boolean {
  return RELOAD_CODES.includes(mascotErrorCode(error) ?? '');
}

/**
 * The sentence for a refused write: 401 and 403 as mascotAuthText, the gallery's own codes in plain words (a duplicate
 * names the outfit that already has the image, from `outfits`, which may include the retired ones and those that never
 * reached the gallery), else the service's message, else `fallback`.
 */
export function mascotErrorText(error: unknown, outfits: ReadonlyArray<Pick<AdminOutfit, 'id' | 'name'>>, fallback: string, action: 'add' | 'restore' | 'other' = 'other'): string {
  const auth = mascotAuthText(error, 'save');
  if (auth) return auth;
  const body = (error as { body?: Record<string, unknown> } | null | undefined)?.body;
  const message = typeof body?.message === 'string' && body.message.trim() ? body.message.trim() : null;
  switch (mascotErrorCode(error)) {
    case 'mascot_changed':
      return MASCOT_CHANGED_TEXT;
    case 'mascot_outfit_not_found':
      return 'That outfit is no longer in the catalog; another admin retired it. The card now shows the catalog as it is.';
    case 'mascot_catalog_full':
      // The total is the only limit now; a limit of 'catalog' comes from a service from before outfits were kept beyond
      // its catalog's 100.
      return body?.limit === 'total'
        ? `Gyro already keeps ${count(MASCOT_LIMITS.maxTotalOutfits)} outfits, the most it can, counting retired ones: they are kept for the costume shop, so retiring an outfit does not make room. Nothing was added.`
        : `The catalog already holds ${MASCOT_LIMITS.maxOutfits} outfits, the most it can at once. Retire one first, then ${action === 'restore' ? 'restore' : 'add'} this one.`;
    case 'mascot_duplicate': {
      const existing = outfits.find((outfit) => outfit.id === body?.outfitId);
      if (body?.retired === true) {
        return `This image is kept as the retired outfit${existing ? ` ${quoted(existing.name)}` : ''}. Restore it under Retired, in Day outfits below, instead of adding the image again.`;
      }
      // `retired: false`: the outfit with this image never reached the gallery (the service names it only then).
      if (body?.retired === false) {
        return `This image is kept as the outfit${existing ? ` ${quoted(existing.name)}` : ''}, which never reached the gallery. Restore it under Needs attention, in Day outfits below, instead of adding the image again.`;
      }
      return existing
        ? `This image is already in the catalog as ${quoted(existing.name)}. Edit that outfit instead of adding the image twice.`
        : 'This image is already in the catalog. Edit that outfit instead of adding the image twice.';
    }
    case 'mascot_not_retired':
      return 'That outfit is already back in the gallery; another admin restored it. The card now shows the gallery as it is.';
    case 'mascot_newer_rule':
      return 'That outfit has a day rule from a newer release of Gyro, so only that release can change or restore it.';
    case 'mascot_not_day_outfit':
      return 'That outfit is not a day outfit: its dates come from its gallery window. The card now shows the gallery as it is.';
    case 'mascot_day_outfit_window':
      return "A day outfit's dates come from its day rule, not a gallery window: change its day under Day outfits instead.";
    case 'mascot_not_featurable':
      return 'A day outfit cannot be featured: on its day it is swapped in for everyone anyway.';
    case 'mascot_audit_failed':
      return message ?? 'Saved, but the audit ledger refused its record just now and it was not written. Tell whoever keeps the audit trail.';
    case 'mascot_too_large':
      return message ?? `The image is too large for the service: a desktop mascot may be at most ${count(MASCOT_LIMITS.maxBytes)} bytes.`;
    default:
      if (message) return message;
      return error instanceof Error && error.message.trim() ? error.message : fallback;
  }
}

// ---- Readiness: which PCs run an agent that can show the gallery ----

/** Compare two dotted agent versions ("0.4.3.8"); null when either is not one. */
export function compareAgentVersions(left: string, right: string): number | null {
  const parse = (value: string) => (/^\d+(\.\d+){0,3}$/.test(value.trim()) ? value.trim().split('.').map(Number) : null);
  const a = parse(left);
  const b = parse(right);
  if (!a || !b) return null;
  for (let i = 0; i < 4; i += 1) {
    const difference = (a[i] ?? 0) - (b[i] ?? 0);
    if (difference !== 0) return difference < 0 ? -1 : 1;
  }
  return 0;
}

type ReadinessDevice = Pick<SupportDevice, 'deviceId' | 'status' | 'purpose' | 'health'>;

/**
 * PCs with an active user key (one PC counts once, however many people use it), and how many of them last reported an
 * agent at or above `minVersion`. A PC that never reported its version is not counted as able.
 */
export function mascotReadiness(
  devices: ReadonlyArray<ReadinessDevice>,
  minVersion: string = MASCOT_MIN_AGENT_VERSION,
): { ready: number; total: number } {
  const pcs = new Map<string, boolean>();
  for (const device of devices) {
    if (device.status !== 'active' || device.purpose !== 'user') continue;
    const version = device.health?.version;
    const able = typeof version === 'string' && (compareAgentVersions(version, minVersion) ?? -1) >= 0;
    pcs.set(device.deviceId, (pcs.get(device.deviceId) ?? false) || able);
  }
  return { ready: [...pcs.values()].filter(Boolean).length, total: pcs.size };
}

/** The gallery readiness: PCs that show each person's pick, and the 0.4.3.8 PCs that show only the featured outfit. */
export function galleryReadiness(devices: ReadonlyArray<ReadinessDevice>): { ready: number; total: number; featuredOnly: number } {
  const gallery = mascotReadiness(devices, MASCOT_MIN_AGENT_VERSION);
  const featured = mascotReadiness(devices, MASCOT_FEATURED_AGENT_VERSION);
  return { ready: gallery.ready, total: gallery.total, featuredOnly: featured.ready - gallery.ready };
}

export type ReadinessRead =
  | { status: 'loading' }
  | { status: 'failed'; httpStatus?: number }
  | { status: 'loaded'; ready: number; total: number; featuredOnly?: number };

export function readinessText(read: ReadinessRead): string {
  if (read.status === 'loading') return 'Checking which PCs can show the gallery…';
  if (read.status === 'failed') {
    return read.httpStatus === 401 || read.httpStatus === 403
      ? 'Which PCs can show the gallery could not be read: the device list is visible to IT admins and auditors only.'
      : 'Which PCs can show the gallery could not be read (the device list did not answer). Reload the page to try again.';
  }
  if (read.total === 0) return 'No PCs are enrolled yet, so no desktop can show the gallery.';
  const featuredOnly = Math.max(0, read.featuredOnly ?? 0);
  const others = read.total - read.ready - featuredOnly;
  return `${read.ready} of ${read.total} active PCs run an agent that can show the gallery (desktop agent ${MASCOT_MIN_AGENT_VERSION} or newer)`
    + (featuredOnly > 0 ? `; ${featuredOnly} more show only the featured outfit (agent ${MASCOT_FEATURED_AGENT_VERSION})` : '')
    + (others > 0 ? '; the others keep the built-in Gyro.' : '.');
}
